import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Modal, ModalContent, useDisclosure } from '@nextui-org/react';
import { useQuery, useMutation } from '@apollo/client';
import { Link, useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Apple,
  Armchair,
  Beef,
  Bike,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Crown,
  Download,
  Droplet,
  Dumbbell,
  Equal,
  Footprints,
  Moon,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Star,
  Sunrise,
  TrendingDown,
  TrendingUp,
  Utensils,
  Wheat,
  X,
  Zap,
} from 'lucide-react';
import { pdf } from '@react-pdf/renderer';

import { GET_ME, GET_DIET_PLAN } from '../graphql/queries';
import { CREATE_GPT, CREATE_DIET_PLAN, UPDATE_DIET_PLAN } from '../graphql/mutations';
import { foodExclusionCategories, foodExclusionCategoriesEn } from '../utils/templates';
import { calculateAll } from '../utils/nutrition';
import { trackPageView, trackEvent } from '../utils/gt';
import { trialContext } from '../utils/subscription';
import { DEFAULT_FREE_MEAL, FREE_MEAL_SLOTS, dayTotalsOf, freeMealBudget, freeMealDayOf, insertFreeMeal, mealTotals, withoutSlot } from '../utils/freeMeal';
import { getDateForDayIndex, getTodayIndex, planFoodToEntry, useWeekCalorieLog } from '../utils/calorieLog';
import { fitMealsToTarget } from '../utils/portions';
import { removeFoodAt, replaceFoodAt } from '../utils/foodEdit';
import { ConfirmDialog } from '../components/app/ConfirmDialog';
import { showUndoToast } from '../utils/undoToast';
import { useWakeLock } from '../utils/useWakeLock';
import { usePageTopOn } from '../utils/pageTop';
import { downloadBlob } from '../utils/downloadBlob';
import { prefersReducedMotion, useCountUp } from '../utils/motion';
import { formatQuantity, macroShares, mainFoods, mealKind, nextMealIndex, sentenceCase } from '../utils/dietPlanView';
import { aiLimitOf, aiLimitText } from '../utils/aiLimits';
import { chooseOption, isOwnPlan, substitutesFor, syncActiveOption } from '../utils/dietImport';

import dietVidEn from '../assets/pro-only-vids/diet-en.mp4';
import dietVidBr from '../assets/pro-only-vids/diet-br.mp4';
import { ModalUpgradePro } from '../components/ModalUpgradePro';
import { ModalTrialRestriction } from '../components/ModalTrialRestriction';
import { DietPlanPDF } from '../components/DietPlanPDF';
import { MealPlate, ProgressRing } from '../components/diet/PlanRings';
import { FoodEditSheet } from '../components/diet/FoodEditSheet';
import { DietStart } from '../components/diet/DietStart';
import { DietCreate } from '../components/diet/DietCreate';
import { DietPoints } from '../components/diet/DietPoints';
import '../styles/pages/dieta.css';
import '../styles/pages/diet-import.css';
import { PreviewArt } from '../components/app/PreviewArt';

const STEPS = [
  'stepPersonalInfo',
  'stepGoal',
  'stepMeals',
  'stepExclusions',
  'stepResults',
  'stepMealPlan',
];
// The questionnaire's steps by name (the URL's ?step), then the plan itself
const STEP = { info: 0, goal: 1, meals: 2, foods: 3, results: 4, plan: 5 };
const WIZARD_STEPS = 5;
const MEAL_COUNTS = [3, 4, 5, 6];

// Plausible ranges for the personal info: anything outside is a typo (178 in the feet box) that would wreck the calorie maths
// Age starts at 18, the Terms' minimum age
const RANGES = { age: [18, 100], kg: [30, 300], lbs: [66, 660], cm: [120, 230], ft: [3, 7], in: [0, 11] };
const inRange = (value, [min, max]) => {
  if (value === '' || value === null || value === undefined) return false;
  const number = Number(value);
  return Number.isFinite(number) && number >= min && number <= max;
};

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT_KEYS = ['monShort', 'tueShort', 'wedShort', 'thuShort', 'friShort', 'satShort', 'sunShort'];

const ACTIVITY_ICONS = {
  sedentary: Armchair,
  light: Footprints,
  moderate: Bike,
  active: Dumbbell,
  veryActive: Zap,
};

// The plan view's icons: each meal's kind (mealKind) and each macro
const MEAL_ICONS = { breakfast: Sunrise, meal: Utensils, snack: Apple, dinner: Moon, free: Star };
const MACRO_ICONS = { protein: Beef, carbs: Wheat, fat: Droplet };

const MealIcon = ({ meal, className }) => {
  const Icon = MEAL_ICONS[mealKind(meal)];
  return <Icon className={className} aria-hidden="true" />;
};

// Desktop opens the day's first meal; phones open none (the layout's own breakpoint)
const isDesktopView = () => typeof window !== 'undefined' && !!window.matchMedia?.('(min-width: 960px)').matches;

const RING_RADIUS = 22;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// Macro ring: `share` is a 0–1 fraction drawn as the arc
const MacroRing = ({ share, label, size = 'md' }) => (
  <span className={`mt-macro-ring mt-macro-ring--${size}`} aria-hidden="true">
    <svg viewBox="0 0 52 52">
      <circle className="mt-macro-ring__track" cx="26" cy="26" r={RING_RADIUS} fill="none" strokeWidth="6" />
      <circle
        className="mt-macro-ring__arc"
        cx="26"
        cy="26"
        r={RING_RADIUS}
        fill="none"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={RING_CIRCUMFERENCE * (1 - Math.min(Math.max(share, 0), 1))}
        style={{ '--mt-ring-full': RING_CIRCUMFERENCE }}
      />
    </svg>
    {label && <span className="mt-macro-ring__label">{label}</span>}
  </span>
);

export const DietPlan = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const {
    isOpen: isUpgradeOpen,
    onOpen: onUpgradeOpen,
    onOpenChange: onUpgradeOpenChange,
    onClose: onUpgradeClose,
  } = useDisclosure();

  const {
    isOpen: isTrialRestrictionOpen,
    onOpen: onTrialRestrictionOpen,
    onOpenChange: onTrialRestrictionOpenChange,
    onClose: onTrialRestrictionClose,
  } = useDisclosure();

  // State — step derived from URL search params
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = parseInt(searchParams.get('step'), 10);
  // The views beside the questionnaire and the plan (?view=): how to get a plan, importing one, building one by hand,
  // and the plan's "Pontos para a consulta"
  const view = ['start', 'import', 'manual', 'points'].includes(searchParams.get('view')) ? searchParams.get('view') : null;
  const currentStep = Number.isFinite(stepParam) && stepParam >= 0 && stepParam < STEPS.length ? stepParam : 0;
  // Each step and view is a screen of its own: it opens at the top, back and forward included
  usePageTopOn(view || `step-${currentStep}`);
  const [gender, setGender] = useState(null);
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [useImperial, setUseImperial] = useState(language !== 'pt');
  const [activityLevel, setActivityLevel] = useState(null);
  const [goal, setGoal] = useState(null);
  const [excludedFoods, setExcludedFoods] = useState([]);
  const [searchExclusions, setSearchExclusions] = useState('');
  const [mealsPerDay, setMealsPerDay] = useState(4);
  // The free meal's day (0 = Monday, null = none) and meal, asked in the questionnaire
  const [freeMealDay, setFreeMealDay] = useState(DEFAULT_FREE_MEAL.day);
  const [freeMealSlot, setFreeMealSlot] = useState(DEFAULT_FREE_MEAL.slot);
  const lastFreeMealDay = useRef(DEFAULT_FREE_MEAL.day);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [nutritionData, setNutritionData] = useState(null);
  // Consent to use the questionnaire's health data (LGPD art. 11): asked before every generation, never pre-ticked
  const [hasHealthConsent, setHasHealthConsent] = useState(false);
  const [isConsentMissing, setIsConsentMissing] = useState(false);

  // State — generation & display
  const [weekPlan, setWeekPlan] = useState(null); // array of 7 day objects
  // Where the plan came from (ai, imported, manual; old plans have none and are the AI's), and what an imported plan
  // brings with it: its substitution list, its notes, and the week as the nutritionist wrote it
  const [origin, setOrigin] = useState(null);
  const [planSubstitutions, setPlanSubstitutions] = useState(null);
  const [originalWeekPlan, setOriginalWeekPlan] = useState(null);
  // A plan being imported or built that isn't saved yet: leaving the page asks first
  const [isDraftDirty, setIsDraftDirty] = useState(false);
  // The plan opens on today (0 = Monday), as the calorie tracker does
  const [selectedDay, setSelectedDay] = useState(getTodayIndex);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  // State — new UI states. The meals opened on the day shown; null = the day's default (openMeals, below)
  const [expandedMeals, setExpandedMeals] = useState(null);
  const [showWeekOverview, setShowWeekOverview] = useState(false);

  // Prevent iOS screen lock during generation
  useWakeLock(isGenerating);

  // The day that couldn't be generated after its retries (the days before it are kept), or null
  const [failedDay, setFailedDay] = useState(null);
  // The API's AI limit that stopped the week (utils/aiLimits): said on the summary for a first day, on the status box
  // for a half-built week. No retries against it
  const [generationLimit, setGenerationLimit] = useState(null);
  // The plan set aside by "Mudar meu plano", so the summary can go back to it without generating a new one
  const [planBeforeChange, setPlanBeforeChange] = useState(null);
  // The weight typed on the summary: it reaches the answers only once it's plausible, so a half-typed "8" can't send
  // the page back to the first step
  const [weightDraft, setWeightDraft] = useState(null);
  // A meal being swapped for another ({ day, meal }), and the last swap, which can be undone
  const [swapping, setSwapping] = useState(null);
  const [lastSwap, setLastSwap] = useState(null);
  // A swap turned down by an AI limit: said under that meal ({ day, meal, limit })
  const [swapLimit, setSwapLimit] = useState(null);
  // A food being changed by hand: where it is ({ day, meal, food } indexes) and the food and meal as the sheet opened
  // on them (kept while the sheet closes)
  const [foodEdit, setFoodEdit] = useState(null);
  const [isFoodEditOpen, setIsFoodEditOpen] = useState(false);
  // The toast that can undo the last food changed: a newer change replaces it
  const foodUndoToastRef = useRef(null);

  // Refs for the generation run (async AI calls read and write them without stale closures)
  const generatedDaysRef = useRef([]);
  const retryCountRef = useRef(0);
  // Each run has an id: a reply from a run that has ended (left the page, started over) is dropped
  const runIdRef = useRef(0);
  // Once the plan is built or changed here, the copy on screen is the newest: a refetch doesn't replace it
  const isLocalPlanRef = useRef(false);
  // The week as last set: a toast's undo runs renders later, and changes the week as it is then
  const weekPlanRef = useRef(weekPlan);
  weekPlanRef.current = weekPlan;

  // State — persisted plan. The ref is what saves read: a day's save can't wait for a render to learn the new id
  const [, setSavedPlanId] = useState(null);
  const savedPlanIdRef = useRef(null);
  const saveChainRef = useRef(Promise.resolve());
  // Whether this plan's answers are saved: the first save of a new plan carries them, the next days only the week
  const questionnaireSavedRef = useRef(false);

  // Query/Mutation
  const { data: dataMe, refetch: refetchMe } = useQuery(GET_ME);
  const { data: dataDietPlan, loading: isLoadingDietPlan, refetch: refetchDietPlan } = useQuery(GET_DIET_PLAN);
  const [createGptMutation] = useMutation(CREATE_GPT);
  const [createDietPlanMutation] = useMutation(CREATE_DIET_PLAN);
  const [updateDietPlanMutation] = useMutation(UPDATE_DIET_PLAN);

  const isPro =
    dataMe?.me?.plan === 'pro' && (dataMe?.me?.subscriptionStatus === 'active' || dataMe?.me?.subscriptionStatus === 'trialing');
  const isTrialing = dataMe?.me?.plan === 'pro' && dataMe?.me?.subscriptionStatus === 'trialing';

  const foodExclusionCategoriesToUse =
    language === 'pt' ? foodExclusionCategories : foodExclusionCategoriesEn;

  // Analytics
  useEffect(() => {
    trackPageView('diet_plan', `${t('appUrl')}/diet`, '/diet');
  }, [t]);

  // Load existing diet plan if available
  useEffect(() => {
    const existingPlan = dataDietPlan?.dietPlan?.data;
    if (!existingPlan || isLocalPlanRef.current) return;

    const { weekPlan: savedWeekPlan, nutritionData: savedNutrition, questionnaire } = existingPlan.attributes;
    setOrigin(existingPlan.attributes.origin || null);
    setPlanSubstitutions(existingPlan.attributes.substitutions || null);
    setOriginalWeekPlan(existingPlan.attributes.originalWeekPlan || null);
    savedPlanIdRef.current = existingPlan.id;
    setSavedPlanId(existingPlan.id);
    questionnaireSavedRef.current = true;

    // Restore questionnaire state
    if (questionnaire) {
      setGender(questionnaire.gender);
      setAge(questionnaire.age);
      setWeight(questionnaire.weight);
      setHeight(questionnaire.height || '');
      setHeightFt(questionnaire.heightFt || '');
      setHeightIn(questionnaire.heightIn || '');
      setUseImperial(questionnaire.useImperial || false);
      setActivityLevel(questionnaire.activityLevel);
      setGoal(questionnaire.goal);
      setExcludedFoods(questionnaire.excludedFoods || []);
      setMealsPerDay(questionnaire.mealsPerDay || 4);
      // Plans saved before the question kept the free meal only in their days
      setFreeMealDay(questionnaire.freeMealDay !== undefined ? questionnaire.freeMealDay : freeMealDayOf(savedWeekPlan));
      setFreeMealSlot(questionnaire.freeMealSlot || DEFAULT_FREE_MEAL.slot);
    }

    if (savedNutrition) {
      setNutritionData(savedNutrition);
    }

    if (savedWeekPlan) {
      setWeekPlan(savedWeekPlan);
      // A link to one of the other views (?view=) stays on it
      if (!view) {
        navigate(`/diet?step=${STEP.plan}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDietPlan]);

  // Leaving the page ends the run: a reply still on its way is saved, but no next day is asked for
  useEffect(
    () => () => {
      runIdRef.current += 1;
    },
    []
  );

  // Unit conversion helpers
  const lbsToKg = (lbs) => lbs * 0.453592;
  const ftInToCm = (ft, inches) => (ft * 12 + inches) * 2.54;

  const getWeightInKg = () =>
    useImperial ? lbsToKg(parseFloat(weight)) : parseFloat(weight);

  const getHeightInCm = () =>
    useImperial
      ? ftInToCm(parseFloat(heightFt) || 0, parseFloat(heightIn) || 0)
      : parseFloat(height);

  const isAgeValid = inRange(age, RANGES.age);
  const isWeightValid = inRange(weight, useImperial ? RANGES.lbs : RANGES.kg);
  const hasValidHeight = useImperial
    ? inRange(heightFt, RANGES.ft) && (heightIn === '' || inRange(heightIn, RANGES.in))
    : inRange(height, RANGES.cm);
  // A field shows its range error once the person leaves it (or tries to move on), not while typing the first digit
  const [touched, setTouched] = useState({});
  const touch = (field) => () => setTouched((previous) => ({ ...previous, [field]: true }));
  const rangeError = {
    age: touched.age && age !== '' && !isAgeValid,
    weight: touched.weight && weight !== '' && !isWeightValid,
    height: touched.height && (useImperial ? heightFt !== '' || heightIn !== '' : height !== '') && !hasValidHeight,
  };

  // Calculate nutrition when moving to results step
  useEffect(() => {
    if (currentStep === STEP.results && gender && age && weight && hasValidHeight && activityLevel && goal) {
      const data = calculateAll(
        getWeightInKg(),
        getHeightInCm(),
        parseInt(age),
        gender,
        activityLevel,
        goal
      );
      setNutritionData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, gender, age, weight, height, heightFt, heightIn, useImperial, activityLevel, goal]);

  const getPreviousDaysFoodSummary = () => {
    const days = generatedDaysRef.current;
    if (days.length === 0) return 'none yet';
    // Include all previous days but limit to 2 foods each to keep the GPT prompt short.
    // By day 7 (Sunday), listing all foods from all 6 previous days made the prompt too long,
    // causing truncated/malformed JSON responses. Keeping all days with fewer foods preserves
    // breadth of variety context (GPT sees every day) while controlling prompt length.
    return days
      .map((day) => {
        const foods = day.meals
          .flatMap((m) => m.foods)
          .map((f) => f.name)
          .slice(0, 2)
          .join(', ');
        return `${day.dayName}: ${foods}`;
      })
      .join('; ');
  };

  // `refresh`: the calorie tracker reads the plan from the cache, so a change to a week still being built refreshes it too
  const persistDietPlan = async (days, { refresh = false } = {}) => {
    const questionnaire = {
      gender,
      age,
      weight,
      height,
      heightFt,
      heightIn,
      useImperial,
      activityLevel,
      goal,
      excludedFoods,
      mealsPerDay,
      freeMealDay,
      freeMealSlot,
    };

    // A new plan's first save carries the answers and the numbers, with the consent the API asks for (health data);
    // after that, each day only adds to the week
    // An AI plan replacing an imported or hand-built one leaves nothing of it behind
    const dietPlanData = questionnaireSavedRef.current
      ? { weekPlan: days }
      : {
          weekPlan: days,
          nutritionData,
          questionnaire,
          origin: 'ai',
          substitutions: null,
          originalWeekPlan: null,
          notes: null,
          healthDataConsent: hasHealthConsent,
        };

    try {
      if (savedPlanIdRef.current) {
        await updateDietPlanMutation({
          variables: { dietPlanInput: dietPlanData, dietPlanId: savedPlanIdRef.current },
        });
      } else {
        const result = await createDietPlanMutation({
          variables: { dietPlanInput: dietPlanData },
        });
        const newId = result?.data?.createDietPlan?.data?.id;
        if (newId) {
          savedPlanIdRef.current = newId;
          setSavedPlanId(newId);
        }
      }
      if (!questionnaireSavedRef.current) {
        setOrigin('ai');
        setPlanSubstitutions(null);
        setOriginalWeekPlan(null);
      }
      questionnaireSavedRef.current = true;
      // Other pages (the calorie tracker) read the plan from the cache: refresh it once the week is whole
      if (refresh || days.length === 7) refetchDietPlan();
    } catch {
      // The plan stays on screen, but say it wasn't saved
      toast.error(t('notifications.dietPlanSaveError'), { toastId: 'diet-plan-save-error' });
    }
  };

  // Saves run one after another: the first day's create has its id before the next day's update goes out
  const saveDietPlan = (days, options) => {
    saveChainRef.current = saveChainRef.current.then(() => persistDietPlan(days, options));
    return saveChainRef.current;
  };

  /** The free meal on this day of the week being generated, or null: its slot, its name and its calorie budget. */
  const freeMealFor = (dayIdx) => {
    if (freeMealDay !== dayIdx || !nutritionData || mealsPerDay < 2) return null;
    return {
      slot: freeMealSlot,
      name: t(`pages.DietPlan.mealSlots.${freeMealSlot}`),
      budget: freeMealBudget(nutritionData.targetCalories, mealsPerDay),
    };
  };

  /** The AI request for one day of the week: the person, the day's calories and macros, what to leave out. */
  const dayPrompt = (dayIdx) => {
    const dayName = t(`pages.DietPlan.${DAY_KEYS[dayIdx]}`);
    const freeMeal = freeMealFor(dayIdx);

    const exclusionsText =
      excludedFoods.length > 0
        ? t('pages.DietPlan.gptExclusions', { foods: excludedFoods.join(', ') })
        : t('pages.DietPlan.gptNoExclusions');

    // On the free meal's day the AI plans the other meals with what's left of the day's target
    const plannedMeals = freeMeal ? mealsPerDay - 1 : mealsPerDay;
    const targetCal = nutritionData.targetCalories - (freeMeal?.budget || 0);
    const share = targetCal / nutritionData.targetCalories;
    const freeMealInstruction = freeMeal
      ? t('pages.DietPlan.gptFreeMealInstruction', { slot: freeMeal.name, meals: plannedMeals })
      : t('pages.DietPlan.gptNoFreeMeal');

    const calPerMeal = plannedMeals > 1 ? Math.round(targetCal / plannedMeals) : targetCal;
    const lastMealCalories = plannedMeals > 1 ? targetCal - calPerMeal * (plannedMeals - 1) : targetCal;

    return t('pages.DietPlan.gptMessage', {
      dayName,
      gender: gender === 'male' ? t('pages.DietPlan.male') : t('pages.DietPlan.female'),
      age,
      weight: Math.round(getWeightInKg()),
      height: Math.round(getHeightInCm()),
      targetCalories: targetCal,
      caloriesPerMeal: calPerMeal,
      lastMealCalories,
      minCalories: targetCal - 50,
      maxCalories: targetCal + 50,
      protein: Math.round(nutritionData.macros.protein * share),
      carbs: Math.round(nutritionData.macros.carbs * share),
      fat: Math.round(nutritionData.macros.fat * share),
      meals: plannedMeals,
      mealsMinusOne: plannedMeals > 1 ? plannedMeals - 1 : 1,
      exclusions: exclusionsText,
      freeMealInstruction,
      previousDays: getPreviousDaysFoodSummary(),
    });
  };

  /** A day from the AI, cleaned up and weighed onto its target; throws when it isn't usable. */
  const dayFromReply = (reply, dayIdx) => {
    const parsed = JSON.parse(reply.createGpt.data.gptResponse);
    const freeMeal = freeMealFor(dayIdx);
    const plannedMeals = freeMeal ? mealsPerDay - 1 : mealsPerDay;

    // Safety net: filter out foods with 0 calories or 0g portion that GPT may return, and the meals left empty
    // (the AI never plans the free meal: it's added below, with its budget)
    if (parsed.meals) {
      parsed.meals = parsed.meals
        .filter((meal) => !meal.isFreeMeal)
        .map((meal) => ({
          ...meal,
          foods: (meal.foods || []).filter((food) => food.calories > 0 && parseFloat(food.quantity) !== 0),
        }))
        .filter((meal) => meal.foods.length > 0);
      // The AI planned the free meal's slot anyway: that meal goes, the free meal takes its place
      if (freeMeal && parsed.meals.length > plannedMeals) parsed.meals = withoutSlot(parsed.meals, freeMeal.slot);
    }

    // Validate meal count is within acceptable range — filtering may drop a meal,
    // so allow plannedMeals-1 through plannedMeals to avoid unnecessary retries
    if (!parsed.meals || parsed.meals.length < plannedMeals - 1 || parsed.meals.length > plannedMeals) {
      throw new Error(`Expected ${plannedMeals} meals but got ${parsed.meals?.length ?? 0}`);
    }
    // Track when a meal was dropped so we can monitor prompt degradation over time
    if (parsed.meals.length < plannedMeals) {
      trackEvent('diet_meal_dropped', `Day ${dayIdx + 1} got ${parsed.meals.length}/${plannedMeals} meals`, 'diet_generation', true);
    }
    // The AI adds up its own calories and overshoots: weigh the day back onto its target (the free meal's budget is outside it)
    parsed.meals = fitMealsToTarget(parsed.meals, nutritionData.targetCalories - (freeMeal?.budget || 0));
    if (freeMeal) parsed.meals = insertFreeMeal(parsed.meals, freeMeal);

    return { dayName: t(`pages.DietPlan.${DAY_KEYS[dayIdx]}`), dayIndex: dayIdx, ...parsed };
  };

  // The week is built a day at a time, in order (each day sees the ones before it, for variety). Every day is shown
  // and saved as it arrives, so a page left or a day that fails loses only what wasn't ready
  const askForDay = (dayIdx, runId) => {
    createGptMutation({
      // The prompt carries the questionnaire's health data to the AI, and the API refuses it without consent: a new
      // questionnaire needs the box ticked (startGeneration), a week being finished was saved with that consent
      variables: { gptInput: { gptMessageToSend: dayPrompt(dayIdx), healthDataConsent: hasHealthConsent || !!savedPlanIdRef.current } },
    }).then(
      ({ data }) => {
        let day;
        try {
          day = dayFromReply(data, dayIdx);
        } catch {
          if (runId === runIdRef.current) retryDay(dayIdx, runId);
          return;
        }
        acceptDay(day, runId);
      },
      (error) => {
        if (runId !== runIdRef.current) return;
        const limit = aiLimitOf(error);
        if (limit) {
          stopAtLimit(dayIdx, limit);
        } else {
          retryDay(dayIdx, runId);
        }
      }
    );
  };

  const acceptDay = (day, runId) => {
    const days = [...generatedDaysRef.current, day];
    generatedDaysRef.current = days;
    retryCountRef.current = 0;
    saveDietPlan(days);
    // Left the page or started over: the day is saved, nothing more is asked for
    if (runId !== runIdRef.current) return;
    setWeekPlan(days);
    setGenerationProgress(days.length);
    // The first day opens the plan; the rest arrive while it's being read
    if (days.length === 1) {
      navigate(`/diet?step=${STEP.plan}`, { replace: true });
    }
    if (days.length < 7) {
      askForDay(days.length, runId);
    } else {
      setIsGenerating(false);
      trackEvent('diet_plan_week_ready', 'Diet Plan - Week Ready', 'diet_plan', true);
    }
  };

  // A day that comes back unusable (or not at all) is asked again, three times; then the run stops at it
  const retryDay = (dayIdx, runId) => {
    if (retryCountRef.current < 3) {
      retryCountRef.current += 1;
      askForDay(dayIdx, runId);
      return;
    }
    retryCountRef.current = 0;
    setIsGenerating(false);
    trackEvent('diet_plan_day_failed', `Diet Plan - Day ${dayIdx + 1} Failed`, 'diet_generation', true);
    if (generatedDaysRef.current.length === 0) {
      toast.error(t('notifications.dietPlanError'));
      navigate(`/diet?step=${STEP.results}`, { replace: true });
    } else {
      setFailedDay(dayIdx);
    }
  };

  // An AI limit stops the run where it is: retrying would only be turned down again. The days ready stay saved
  const stopAtLimit = (dayIdx, limit) => {
    retryCountRef.current = 0;
    setIsGenerating(false);
    setGenerationLimit(limit);
    trackEvent('diet_plan_ai_limit', `Diet Plan - AI Limit ${limit.code} (${limit.scope})`, 'diet_generation', true);
    if (generatedDaysRef.current.length === 0) {
      navigate(`/diet?step=${STEP.results}`, { replace: true });
    } else {
      setFailedDay(dayIdx);
    }
  };

  /** Starts asking for the days after `days` (none for a new plan). */
  const startRun = (days) => {
    runIdRef.current += 1;
    isLocalPlanRef.current = true;
    generatedDaysRef.current = days;
    retryCountRef.current = 0;
    setFailedDay(null);
    setGenerationLimit(null);
    setIsGenerating(true);
    setGenerationProgress(days.length);
    askForDay(days.length, runIdRef.current);
  };

  const handleExportPDF = async () => {
    if (isTrialing) {
      onTrialRestrictionOpen();
      return;
    }
    trackEvent('diet_plan_export_pdf', 'Diet Plan - Export PDF', 'diet_plan', true);
    setIsExportingPDF(true);
    try {
      const blob = await pdf(
        <DietPlanPDF
          weekPlan={weekPlan}
          weeklyAverages={weeklyAverages}
          language={language}
          targetCalories={nutritionData?.targetCalories}
          goalLabel={goal ? t(`pages.DietPlan.${goal}`) : undefined}
        />
      ).toBlob();
      downloadBlob(blob, `${t('pages.DietPlan.exportFilename')}.pdf`);
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleGenerateWeekPlan = () => {
    if (!nutritionData) return;

    trackEvent('diet_plan_generate_week', 'Diet Plan - Generate Week', 'diet_plan', true);

    // A new plan: its answers go with the first day saved
    questionnaireSavedRef.current = false;
    setPlanBeforeChange(null);
    setWeekPlan(null);
    setSelectedDay(0);
    setExpandedMeals(new Set([0]));
    setShowWeekOverview(false);
    startRun([]);
  };

  // Finishes a week left unfinished (a day that failed, or a page left mid-way), keeping the days that are ready
  const resumeGeneration = () => {
    trackEvent('diet_plan_resume', 'Diet Plan - Resume Generation', 'diet_plan', true);
    questionnaireSavedRef.current = true;
    startRun(weekPlan || []);
  };

  /** The week with one meal replaced, shown and saved (the week only: the answers don't change). */
  const replaceMeal = (dayIdx, mealIdx, meal) => {
    const days = weekPlanRef.current.map((day, index) =>
      index === dayIdx ? { ...day, meals: day.meals.map((current, position) => (position === mealIdx ? meal : current)) } : day
    );
    weekPlanRef.current = days;
    isLocalPlanRef.current = true;
    setWeekPlan(days);
    saveDietPlan(days, { refresh: true });
  };

  // A meal the person doesn't fancy, swapped for another with the same calories and macros: one AI call, not a new week
  const swapMeal = async (dayIdx, mealIdx) => {
    const meal = weekPlan[dayIdx].meals[mealIdx];
    const totals = mealTotals(meal);
    trackEvent('diet_plan_swap_meal', 'Diet Plan - Swap Meal', 'diet_plan', true);
    setSwapping({ day: dayIdx, meal: mealIdx });
    setSwapLimit(null);
    try {
      const { data } = await createGptMutation({
        variables: {
          gptInput: {
            gptMessageToSend: t('pages.DietPlan.gptSwapMeal', {
              meal: meal.name,
              current: meal.foods.map((food) => `${food.name} ${food.quantity}`).join(', '),
              minCalories: Math.round(totals.calories) - 15,
              maxCalories: Math.round(totals.calories) + 15,
              protein: Math.round(totals.protein),
              carbs: Math.round(totals.carbs),
              fat: Math.round(totals.fat),
              exclusions:
                excludedFoods.length > 0
                  ? t('pages.DietPlan.gptExclusions', { foods: excludedFoods.join(', ') })
                  : t('pages.DietPlan.gptNoExclusions'),
            }),
            // The foods left out go with the prompt, and the API asks for consent on every diet prompt: the plan on
            // screen was saved with it
            healthDataConsent: !!savedPlanIdRef.current,
          },
        },
      });
      const parsed = JSON.parse(data.createGpt.data.gptResponse);
      const foods = (parsed.foods || []).filter((food) => food.calories > 0 && parseFloat(food.quantity) !== 0);
      if (foods.length === 0) throw new Error('No foods');
      // Weighed onto the old meal's calories, as the days are onto theirs
      const [swapped] = fitMealsToTarget([{ name: meal.name, foods }], Math.round(totals.calories));
      replaceMeal(dayIdx, mealIdx, swapped);
      setLastSwap({ day: dayIdx, meal: mealIdx, previous: meal });
    } catch (error) {
      // A limit is said under the meal, in its words; anything else is the usual toast
      const limit = aiLimitOf(error);
      if (limit) {
        setSwapLimit({ day: dayIdx, meal: mealIdx, limit });
      } else {
        toast.error(ui('swapError'), { toastId: 'diet-plan-swap-error' });
      }
    } finally {
      setSwapping(null);
    }
  };

  const undoSwap = () => {
    trackEvent('diet_plan_undo_swap', 'Diet Plan - Undo Swap', 'diet_plan', true);
    replaceMeal(lastSwap.day, lastSwap.meal, lastSwap.previous);
    setLastSwap(null);
  };

  // ── A food changed by hand: swapped for another, a new amount, or taken out. No AI ──

  const openFoodEdit = (dayIdx, mealIdx, foodIdx) => {
    const meal = weekPlan[dayIdx].meals[mealIdx];
    trackEvent('diet_plan_food_edit_open', 'Diet Plan - Open Food Edit', 'diet_plan', true);
    setFoodEdit({ day: dayIdx, meal: mealIdx, food: foodIdx, item: meal.foods[foodIdx], mealFoods: meal.foods, mealName: mealName(meal) });
    setIsFoodEditOpen(true);
  };

  /**
   * The meal with the food changed, shown and saved like a swapped meal, and a toast to take it back. The undo puts back
   * the meal as it was, while the meal is still as this change left it; only the newest change keeps its toast.
   */
  const changeFood = (changedMeal, { event, label, message }) => {
    // A meal with options keeps the one shown in step, so switching away and back keeps the change
    const changed = syncActiveOption(changedMeal);
    const { day, meal: mealIdx } = foodEdit;
    const previous = weekPlanRef.current[day].meals[mealIdx];
    trackEvent(event, label, 'diet_plan', true);
    replaceMeal(day, mealIdx, changed);
    setIsFoodEditOpen(false);
    // The swap's undo would now take this change away with it: the toast is the way back
    if (lastSwap?.day === day && lastSwap?.meal === mealIdx) setLastSwap(null);
    if (foodUndoToastRef.current != null) toast.dismiss(foodUndoToastRef.current);
    foodUndoToastRef.current = showUndoToast(ui(message), {
      undoLabel: ui('undo'),
      onUndo: () => {
        if (weekPlanRef.current?.[day]?.meals?.[mealIdx] !== changed) return;
        trackEvent('diet_plan_food_undo', 'Diet Plan - Undo Food Edit', 'diet_plan', true);
        replaceMeal(day, mealIdx, previous);
      },
    });
  };

  const editedMeal = () => weekPlanRef.current[foodEdit.day].meals[foodEdit.meal];
  const swapFood = (food) =>
    changeFood(replaceFoodAt(editedMeal(), foodEdit.food, food), { event: 'diet_plan_food_swap', label: 'Diet Plan - Swap Food', message: 'foodSwappedToast' });
  const changeFoodAmount = (food) =>
    changeFood(replaceFoodAt(editedMeal(), foodEdit.food, food), { event: 'diet_plan_food_amount', label: 'Diet Plan - Change Food Amount', message: 'foodAmountToast' });
  const removeFood = () =>
    changeFood(removeFoodAt(editedMeal(), foodEdit.food), { event: 'diet_plan_food_remove', label: 'Diet Plan - Remove Food', message: 'foodRemovedToast' });
  // "Remover" in the food sheet asks first; the undo toast still follows
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);

  const toggleFoodExclusion = (food) => {
    setExcludedFoods((prev) =>
      prev.includes(food) ? prev.filter((f) => f !== food) : [...prev, food]
    );
  };

  const canProceedStep0 = gender && isAgeValid && isWeightValid && hasValidHeight && activityLevel;
  const canProceedStep1 = goal;

  // A step opened from the summary's "Editar": its one action goes back to the summary
  const isEditing = searchParams.get('edit') === '1';
  const editSection = (step) => {
    trackEvent(`diet_plan_edit_${STEPS[step]}`, `Diet Plan - Edit ${STEPS[step]}`, 'diet_plan', true);
    setSearchParams({ step, edit: '1' });
  };

  const handleNext = () => {
    if (currentStep === STEP.info && !canProceedStep0) {
      setTouched({ age: true, weight: true, height: true });
      const isMissing = !gender || !age || !weight || !(useImperial ? heightFt : height) || !activityLevel;
      return toast.error(t(isMissing ? 'notifications.dietPlanFieldsRequired' : 'notifications.dietPlanCheckValues'));
    }
    if (currentStep === STEP.goal && !canProceedStep1) {
      return toast.error(t('notifications.dietPlanFieldsRequired'));
    }
    trackEvent(
      `diet_plan_step_${currentStep}_next`,
      `Diet Plan - Step ${currentStep} Next`,
      'diet_plan',
      true
    );
    if (isEditing) {
      setSearchParams({ step: STEP.results }, { replace: true });
    } else {
      setSearchParams({ step: currentStep + 1 });
    }
  };

  const handleBack = () => {
    if (currentStep <= 0) return;
    if (currentStep <= 1) {
      navigate(`/diet?step=${STEP.info}`, { replace: true });
    } else {
      navigate(-1);
    }
  };

  // A reload keeps only ?step: send the user back to the first step whose answers are missing
  useEffect(() => {
    if (isLoadingDietPlan || isGenerating || view) return;
    // The entry (no ?step) isn't a questionnaire step
    if (!searchParams.has('step')) return;
    // A saved plan is applied by the effect above in this same commit: wait for it, don't redirect past it
    if (dataDietPlan?.dietPlan?.data?.attributes?.weekPlan && !weekPlan) return;
    let validStep = currentStep;
    if (currentStep > STEP.info && !canProceedStep0) validStep = STEP.info;
    else if (currentStep > STEP.goal && !canProceedStep1) validStep = STEP.goal;
    else if (currentStep === STEP.plan && !weekPlan) validStep = nutritionData ? STEP.results : STEP.info;
    if (validStep !== currentStep) setSearchParams({ step: validStep }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingDietPlan, isGenerating, currentStep, canProceedStep0, canProceedStep1, weekPlan, nutritionData, dataDietPlan, view]);

  // Leaving while the week is being generated loses it: ask first
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => (isGenerating || isDraftDirty) && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (!isGenerating) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isGenerating]);

  // Activity level options
  const activityOptions = [
    { key: 'sedentary', label: t('pages.DietPlan.sedentary'), desc: t('pages.DietPlan.sedentaryDesc') },
    { key: 'light', label: t('pages.DietPlan.light'), desc: t('pages.DietPlan.lightDesc') },
    { key: 'moderate', label: t('pages.DietPlan.moderate'), desc: t('pages.DietPlan.moderateDesc') },
    { key: 'active', label: t('pages.DietPlan.active'), desc: t('pages.DietPlan.activeDesc') },
    { key: 'veryActive', label: t('pages.DietPlan.veryActive'), desc: t('pages.DietPlan.veryActiveDesc') },
  ];

  // Goal options
  const goalOptions = [
    { key: 'lose', label: t('pages.DietPlan.lose'), desc: t('pages.DietPlan.loseDesc'), Icon: TrendingDown },
    { key: 'maintain', label: t('pages.DietPlan.maintain'), desc: t('pages.DietPlan.maintainDesc'), Icon: Equal },
    { key: 'gain', label: t('pages.DietPlan.gain'), desc: t('pages.DietPlan.gainDesc'), Icon: TrendingUp },
  ];

  // ── Computed data for the meal plan display ──

  const readyDays = weekPlan?.length || 0;
  const isWeekComplete = readyDays >= 7;

  // A week whose day for today isn't built yet opens on its first day, before it paints
  useLayoutEffect(() => {
    if (weekPlan?.length && !weekPlan[selectedDay]) setSelectedDay(0);
  }, [weekPlan, selectedDay]);

  const selectedDayData = useMemo(() => {
    if (!weekPlan?.[selectedDay]) return null;
    const day = weekPlan[selectedDay];
    const mealsWithTotals = day.meals.map((meal) => ({ ...meal, totals: mealTotals(meal) }));
    return { ...day, mealsWithTotals, dayTotals: dayTotalsOf(day.meals) };
  }, [weekPlan, selectedDay]);

  const weeklyAverages = useMemo(() => {
    if (!weekPlan || weekPlan.length === 0) return null;
    const totals = weekPlan.reduce(
      (acc, day) => {
        const dayTotal = dayTotalsOf(day.meals);
        return {
          calories: acc.calories + dayTotal.calories,
          protein: acc.protein + dayTotal.protein,
          carbs: acc.carbs + dayTotal.carbs,
          fat: acc.fat + dayTotal.fat,
        };
      },
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );
    const count = weekPlan.length;
    return {
      calories: Math.round(totals.calories / count),
      protein: Math.round(totals.protein / count),
      carbs: Math.round(totals.carbs / count),
      fat: Math.round(totals.fat / count),
    };
  }, [weekPlan]);

  // Per-day totals for week overview cards
  const dayTotals = useMemo(() => (weekPlan || []).map((day) => ({ ...day, totals: dayTotalsOf(day.meals) })), [weekPlan]);

  const ui = (key, options) => t(`pages.DietPlan.ui.${key}`, options);
  const diet = (key, options) => t(`pages.DietPlan.${key}`, options);
  // The plan view reads the free meal from the saved data, not from the questionnaire
  const hasFreeMeal = (day) => !!day?.meals?.some((meal) => meal.isFreeMeal);
  const locale = language === 'pt' ? 'pt-BR' : 'en-US';
  const formatKcal = (value) => Math.round(value).toLocaleString(locale);

  // The plan's day N is this week's day N in the calorie tracker: what's logged there shows here, and a planned meal
  // can be logged from here in one tap. Days still to come can't be logged yet
  const calorieLog = useWeekCalorieLog({ skip: !weekPlan });
  const todayIndex = getTodayIndex();
  const canLogDay = selectedDay <= todayIndex;
  const selectedDate = getDateForDayIndex(selectedDay);
  const entriesOf = (dayIndex) => calorieLog.getLogForDate(getDateForDayIndex(dayIndex))?.attributes?.entries || [];
  const loggedEntries = entriesOf(selectedDay);
  const loggedKcal = (mealIndex) =>
    loggedEntries.filter((entry) => mealIndex === undefined || (entry.mealIndex ?? 0) === mealIndex).reduce((sum, entry) => sum + (entry.calories || 0), 0);
  const isMealLogged = (mealIndex) => loggedEntries.some((entry) => (entry.mealIndex ?? 0) === mealIndex);
  // Today's next meal (the first one with nothing in the tracker), or -1: other days have none
  const nextMeal = selectedDay === todayIndex && selectedDayData ? nextMealIndex(selectedDayData.meals, isMealLogged) : -1;
  // The day's ring holds what's in the tracker; a day still to come holds its plan. Both count to their value
  const shownDayKcal = useCountUp(Math.round(canLogDay ? loggedKcal() : selectedDayData?.dayTotals?.calories ?? 0));
  const shownWeekKcal = useCountUp(Math.round(weeklyAverages?.calories ?? 0));
  const trackerLink = (mealIndex, add) =>
    `/calorie-tracker?day=${selectedDay}${mealIndex !== undefined ? `&meal=${mealIndex}` : ''}${add ? '&add=1' : ''}`;

  const logPlannedMeal = async (meal, mealIndex) => {
    trackEvent('diet_plan_log_meal', 'Diet Plan - Log Planned Meal', 'diet_plan', true);
    try {
      await calorieLog.addEntries(selectedDate, meal.foods.map((food) => planFoodToEntry(food, mealIndex)));
      toast.success(ui('mealLoggedToast'));
    } catch {
      toast.error(t('notifications.calorieEntryError'));
    }
  };
  const dayLabel = (index) => diet(DAY_KEYS[index]);
  // This week's date for the plan's day: "Quinta, 29 de outubro" (the short form "Quinta, 29")
  const dayDate = (index) => {
    const [year, month, date] = getDateForDayIndex(index).split('-').map(Number);
    return new Date(year, month - 1, date);
  };
  const dayTitle = (index) => ui('dayTitle', { day: dayLabel(index), date: dayDate(index).toLocaleDateString(locale, { day: 'numeric', month: 'long' }) });
  const dayTitleShort = (index) => ui('dayTitle', { day: dayLabel(index), date: dayDate(index).getDate() });
  const goalLabel = goal ? diet(goal === 'lose' ? 'lose' : goal === 'gain' ? 'gain' : 'maintain') : '';

  // A new plan starts from the summary of the answers already given: each one is a tap away, nothing to step through
  const openSummary = () => {
    // The answers are being changed here now: a refetch of the saved plan mustn't put the old ones back
    isLocalPlanRef.current = true;
    setPlanBeforeChange(weekPlan);
    // Consent is asked before every generation, never carried over ticked
    setHasHealthConsent(false);
    setWeekPlan(null);
    navigate(`/diet?step=${STEP.results}`, { replace: true });
  };

  // "Mudar meu plano" offers the three ways again; the plan stays until a new one is saved
  const changePlan = () => {
    trackEvent('diet_plan_change_plan', 'Diet Plan - Change Plan', 'diet_plan', true);
    navigate('/diet?view=start');
  };

  const hasSavedPlan = !!weekPlan?.length || !!dataDietPlan?.dietPlan?.data;
  const goToView = (next) => {
    setSearchParams({ view: next });
  };

  // The entry's three ways. The AI's opens the questionnaire, or its summary when the answers are already there
  const startWithAi = () => {
    trackEvent('diet_plan_entry_ai', 'Diet Plan - Entry AI', 'diet_plan', true);
    if (gender && age && weight && activityLevel && goal) {
      openSummary();
      return;
    }
    if (weekPlan) setPlanBeforeChange(weekPlan);
    setSearchParams({ step: STEP.info });
  };
  const startImport = () => {
    trackEvent('diet_plan_entry_import', 'Diet Plan - Entry Import', 'diet_plan', true);
    goToView('import');
  };
  const startManual = () => {
    trackEvent('diet_plan_entry_manual', 'Diet Plan - Entry Manual', 'diet_plan', true);
    goToView('manual');
  };
  const backFromView = () => {
    if (view === 'import' || view === 'manual') {
      navigate('/diet?view=start', { replace: true });
    } else {
      navigate(`/diet?step=${STEP.plan}`, { replace: true });
    }
  };
  const backToPlanView = () => {
    navigate(hasSavedPlan ? `/diet?step=${STEP.plan}` : '/diet', { replace: true });
  };

  const openPoints = () => {
    navigate('/diet?view=points');
  };

  /**
   * An imported or hand-built plan, saved as the account's plan: it replaces the one there is (the same record, so its
   * questionnaire stays for the analysis' goal), and opens on today.
   */
  const saveOwnPlan = async (payload) => {
    let id = savedPlanIdRef.current;
    if (id) {
      await updateDietPlanMutation({ variables: { dietPlanInput: payload, dietPlanId: id } });
    } else {
      const result = await createDietPlanMutation({ variables: { dietPlanInput: payload } });
      id = result?.data?.createDietPlan?.data?.id;
      if (!id) throw new Error('No plan id');
    }
    runIdRef.current += 1;
    savedPlanIdRef.current = id;
    setSavedPlanId(id);
    isLocalPlanRef.current = true;
    questionnaireSavedRef.current = true;
    setIsDraftDirty(false);
    setWeekPlan(payload.weekPlan);
    setNutritionData(payload.nutritionData);
    setOrigin(payload.origin);
    setPlanSubstitutions(payload.substitutions);
    setOriginalWeekPlan(payload.originalWeekPlan);
    setPlanBeforeChange(null);
    setLastSwap(null);
    setSelectedDay(getTodayIndex());
    setExpandedMeals(null);
    setShowWeekOverview(false);
    refetchDietPlan();
    navigate(`/diet?step=${STEP.plan}`, { replace: true });
  };

  // A week changed from the analysis (one of the plan's own options or substitutions, its undo, or back to the
  // nutritionist's plan): shown and saved, the week only
  const changeWeek = (days) => {
    weekPlanRef.current = days;
    isLocalPlanRef.current = true;
    setWeekPlan(days);
    saveDietPlan(days, { refresh: true });
  };

  const backToPlan = () => {
    trackEvent('diet_plan_back_to_plan', 'Diet Plan - Back to Plan', 'diet_plan', true);
    setWeekPlan(planBeforeChange);
    setPlanBeforeChange(null);
    navigate(`/diet?step=${STEP.plan}`, { replace: true });
  };

  const startGeneration = () => {
    if (!isPro) {
      trackEvent('diet_plan_upgrade_modal', 'Diet Plan - Upgrade Modal Open', 'diet_plan', true);
      onUpgradeOpen();
      return;
    }
    if (!hasHealthConsent) {
      setIsConsentMissing(true);
      toast.error(t('notifications.dietPlanConsentRequired'), { toastId: 'diet-plan-consent' });
      return;
    }
    handleGenerateWeekPlan();
  };

  // A day picked from the rings or the week list: its meals open as the day's default (openMeals). From the week
  // list on phones, the page goes back up to the day's plate
  const showDay = (index, { scrollUp = false } = {}) => {
    setSelectedDay(index);
    setShowWeekOverview(false);
    setExpandedMeals(null);
    if (scrollUp && !isDesktopView()) window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  // The week's seven days, each in a ring of what the tracker holds against the target. The days not built yet wait
  // in place (dashed), the one being built with a spinner
  const weekRings = (
    <div className="mt-dp-days" role="tablist" aria-label={ui('dayPills')}>
      {DAY_KEYS.map((key, index) => {
        const day = weekPlan?.[index];
        const isToday = index === todayIndex;
        const isFree = hasFreeMeal(day);
        const tracked = day && index <= todayIndex && nutritionData ? entriesOf(index).reduce((sum, entry) => sum + (entry.calories || 0), 0) : 0;
        const label = [dayTitle(index), isToday && ui('tabToday'), isFree && ui('tabFree')].filter(Boolean).join(', ');
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={!showWeekOverview && selectedDay === index}
            aria-label={label}
            className={`mt-dp-day${isToday ? ' is-today' : ''}`}
            disabled={!day}
            onClick={() => showDay(index)}
          >
            <ProgressRing value={nutritionData ? tracked / nutritionData.targetCalories : 0} size={38} stroke={4.5} track="gray" className="mt-dp-day__ring">
              {!day && isGenerating && index === readyDays ? (
                <span className="mt-dp-spinner" aria-hidden="true" />
              ) : (
                <span className="mt-dp-day__date">{dayDate(index).getDate()}</span>
              )}
            </ProgressRing>
            <span className="mt-dp-day__name">
              {diet(DAY_SHORT_KEYS[index])}
              {isFree && <Star className="mt-dp-star" aria-hidden="true" />}
            </span>
          </button>
        );
      })}
    </div>
  );

  // A week still being built, or left unfinished: how far it got, and the way to finish it (under the box on
  // phones, at the card's end on desktop)
  const renderBuilding = () => {
    if (isWeekComplete) return null;
    if (isGenerating) {
      return (
        <section className="mt-dp-status" role="status">
          <div className="mt-dp-status__box">
            <span className="mt-dp-status__icon" aria-hidden="true">
              <span className="mt-dp-spinner" />
            </span>
            <div>
              <p className="mt-dp-status__title">{ui('buildingTitle', { ready: readyDays, total: 7 })}</p>
              <p className="mt-dp-status__sub">{ui('buildingSub')}</p>
            </div>
          </div>
        </section>
      );
    }
    const isFailed = failedDay !== null;
    const failedName = isFailed ? diet(DAY_KEYS[failedDay]) : '';
    // Stopped by an AI limit: which one and when it lifts. A daily one says when to come back instead of a retry
    if (isFailed && generationLimit) {
      const limitText = aiLimitText(t, generationLimit, { locale });
      return (
        <section className="mt-dp-status" role="alert">
          <div className="mt-dp-status__box">
            <span className="mt-dp-status__icon" aria-hidden="true">
              <CircleAlert />
            </span>
            <div>
              <p className="mt-dp-status__title">{limitText.title}</p>
              <p className="mt-dp-status__sub">{limitText.body}</p>
            </div>
          </div>
          {limitText.canRetry ? (
            <button type="button" className="mt-btn mt-btn--primary mt-dp-status__action" onClick={resumeGeneration}>
              <RefreshCw aria-hidden="true" />
              {ui('tryAgain')}
            </button>
          ) : (
            <p className="mt-dp-status__resume">{limitText.resume}</p>
          )}
        </section>
      );
    }
    return (
      <section className="mt-dp-status">
        <div className="mt-dp-status__box">
          <span className="mt-dp-status__icon" aria-hidden="true">
            {isFailed ? <CircleAlert /> : <CalendarDays />}
          </span>
          <div>
            <p className="mt-dp-status__title">
              {isFailed
                ? ui('buildingFailed', { day: language === 'pt' ? failedName.toLowerCase() : failedName })
                : ui('buildingPaused', { ready: readyDays, total: 7 })}
            </p>
            <p className="mt-dp-status__sub">{ui(isFailed ? 'buildingFailedSub' : 'buildingPausedSub')}</p>
          </div>
        </div>
        <button type="button" className="mt-btn mt-btn--primary mt-dp-status__action" onClick={resumeGeneration}>
          {isFailed && <RefreshCw aria-hidden="true" />}
          {ui(isFailed ? 'tryAgain' : 'continueBuilding')}
        </button>
      </section>
    );
  };

  const macroRows = (macros, { asRows = false } = {}) => {
    const items = [
      { key: 'protein', grams: macros.protein, perGram: 4 },
      { key: 'carbs', grams: macros.carbs, perGram: 4 },
      { key: 'fat', grams: macros.fat, perGram: 9 },
    ];
    const totalKcal = items.reduce((total, item) => total + item.grams * item.perGram, 0) || 1;
    return (
      <ul className={`mt-macro-list${asRows ? ' mt-macro-list--rows' : ''}`}>
        {items.map(({ key, grams, perGram }) => {
          const share = (grams * perGram) / totalKcal;
          return (
            <li key={key}>
              <MacroRing share={share} label={`${Math.round(share * 100)}%`} />
              <span className="mt-macro-list__text">
                <span className="mt-macro-list__name">{diet(key)}</span>
                <span className="mt-macro-list__grams">
                  {Math.round(grams)} {diet('grams')}
                </span>
                <span className="mt-macro-list__share">{ui('shareOfCalories', { pct: Math.round(share * 100) })}</span>
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  // ── Wizard (steps 0–4) ────────────────────────────────────────────────

  const renderPersonalInfo = () => (
    <div className="mt-diet-form">
      {/* Side by side on desktop, where the form has the page's width */}
      <div className="mt-diet-pair">
        <fieldset className="mt-diet-field">
          <legend className="mt-form-field__label">{diet('gender')}</legend>
          <div className="mt-segment">
            {['male', 'female'].map((key) => (
              <button key={key} type="button" aria-pressed={gender === key} onClick={() => setGender(key)}>
                {diet(key)}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="mt-diet-field">
          <legend className="mt-form-field__label">{ui('units')}</legend>
          <div className="mt-segment">
            <button type="button" aria-pressed={!useImperial} onClick={() => setUseImperial(false)}>
              {diet('metric')}
            </button>
            <button type="button" aria-pressed={useImperial} onClick={() => setUseImperial(true)}>
              {diet('imperial')}
            </button>
          </div>
        </fieldset>
      </div>
      <div className="mt-diet-numbers">
        <label className="mt-form-field">
          <span className="mt-form-field__label">{diet('age')}</span>
          <input
            type="number"
            inputMode="numeric"
            min={RANGES.age[0]}
            max={RANGES.age[1]}
            value={age}
            onChange={(event) => setAge(event.target.value)}
            onBlur={touch('age')}
            aria-invalid={rangeError.age || undefined}
          />
          {rangeError.age && <span className="mt-form-field__error">{ui('ageRange', { min: RANGES.age[0], max: RANGES.age[1] })}</span>}
        </label>
        <label className="mt-form-field">
          <span className="mt-form-field__label">
            {diet('weight')} ({useImperial ? diet('lbs') : diet('kg')})
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={(useImperial ? RANGES.lbs : RANGES.kg)[0]}
            max={(useImperial ? RANGES.lbs : RANGES.kg)[1]}
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
            onBlur={touch('weight')}
            aria-invalid={rangeError.weight || undefined}
          />
          {rangeError.weight && (
            <span className="mt-form-field__error">
              {ui('weightRange', {
                min: (useImperial ? RANGES.lbs : RANGES.kg)[0],
                max: (useImperial ? RANGES.lbs : RANGES.kg)[1],
                unit: useImperial ? diet('lbs') : diet('kg'),
              })}
            </span>
          )}
        </label>
        {useImperial ? (
          <div className="mt-diet-height">
            <label className="mt-form-field">
              <span className="mt-form-field__label">
                {diet('height')} ({diet('ft')})
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={RANGES.ft[0]}
                max={RANGES.ft[1]}
                value={heightFt}
                onChange={(event) => setHeightFt(event.target.value)}
                onBlur={touch('height')}
                aria-invalid={rangeError.height || undefined}
              />
              {rangeError.height && (
                <span className="mt-form-field__error">
                  {ui('heightRangeFt', { minFt: RANGES.ft[0], maxFt: RANGES.ft[1], maxIn: RANGES.in[1] })}
                </span>
              )}
            </label>
            <label className="mt-form-field">
              <span className="mt-form-field__label">{diet('in')}</span>
              <input
                type="number"
                inputMode="numeric"
                min={RANGES.in[0]}
                max={RANGES.in[1]}
                value={heightIn}
                onChange={(event) => setHeightIn(event.target.value)}
                onBlur={touch('height')}
                aria-invalid={rangeError.height || undefined}
              />
            </label>
          </div>
        ) : (
          <label className="mt-form-field">
            <span className="mt-form-field__label">
              {diet('height')} ({diet('cm')})
            </span>
            <input
              type="number"
              inputMode="numeric"
              min={RANGES.cm[0]}
              max={RANGES.cm[1]}
              value={height}
              onChange={(event) => setHeight(event.target.value)}
              onBlur={touch('height')}
              aria-invalid={rangeError.height || undefined}
            />
            {rangeError.height && <span className="mt-form-field__error">{ui('heightRangeCm', { min: RANGES.cm[0], max: RANGES.cm[1] })}</span>}
          </label>
        )}
      </div>
      <fieldset className="mt-diet-field">
        <legend className="mt-form-field__label">{diet('activityLevel')}</legend>
        <ul className="mt-option-list mt-diet-activity">
          {activityOptions.map((option) => {
            const Icon = ACTIVITY_ICONS[option.key];
            return (
              <li key={option.key}>
                <button
                  type="button"
                  className="mt-option mt-option--mode"
                  aria-pressed={activityLevel === option.key}
                  onClick={() => {
                    setActivityLevel(option.key);
                    trackEvent(`diet_plan_activity_${option.key}`, `Diet Plan - Activity: ${option.key}`, 'diet_plan', true);
                  }}
                >
                  <span className="mt-option__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="mt-option__text">
                    <span className="mt-option__title">{option.label}</span>
                    <span className="mt-option__desc">{option.desc}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </fieldset>
    </div>
  );

  const renderGoal = () => (
    <fieldset className="mt-diet-field">
      <legend className="mt-sr-only">{diet('goal')}</legend>
      <ul className="mt-option-list mt-diet-goals">
        {goalOptions.map(({ key, label, desc, Icon }) => (
          <li key={key}>
            <button
              type="button"
              className="mt-option mt-option--mode"
              aria-pressed={goal === key}
              onClick={() => {
                setGoal(key);
                trackEvent(`diet_plan_goal_${key}`, `Diet Plan - Goal: ${key}`, 'diet_plan', true);
              }}
            >
              <span className="mt-option__icon" aria-hidden="true">
                <Icon />
              </span>
              <span className="mt-option__text">
                <span className="mt-option__title">{label}</span>
                <span className="mt-option__desc">{desc}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </fieldset>
  );

  // How many meals a day, and whether one of the week's is free (which day, lunch or dinner)
  const renderMeals = () => {
    const hasFreeMeal = freeMealDay !== null;
    return (
      <div className="mt-diet-form">
        <fieldset className="mt-diet-field">
          <legend className="mt-form-field__label">{ui('mealsCountQuestion')}</legend>
          <ul className="mt-option-list mt-diet-counts">
            {MEAL_COUNTS.map((count) => (
              <li key={count}>
                <button type="button" className="mt-option mt-diet-count" aria-pressed={mealsPerDay === count} onClick={() => setMealsPerDay(count)}>
                  <span className="mt-diet-count__n">{count}</span>
                  <span className="mt-option__text">
                    <span className="mt-option__title">{ui('mealsCountOption', { count })}</span>
                    <span className="mt-option__desc">{ui(`mealsCountDesc${count}`)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </fieldset>

        <div className="mt-diet-field">
          <label className="mt-switch-row mt-diet-free-switch">
            <Star className="mt-switch-row__icon" aria-hidden="true" />
            <span className="mt-switch-row__text">
              <span className="mt-switch-row__label">{ui('freeMealSwitch')}</span>
              <span className="mt-switch-row__desc">{ui('freeMealSwitchDesc')}</span>
            </span>
            <input
              type="checkbox"
              role="switch"
              checked={hasFreeMeal}
              onChange={(event) => {
                if (event.target.checked) setFreeMealDay(lastFreeMealDay.current);
                else {
                  lastFreeMealDay.current = freeMealDay;
                  setFreeMealDay(null);
                }
              }}
            />
          </label>
          {hasFreeMeal && (
            <div className="mt-diet-free">
              <fieldset className="mt-diet-free__group">
                <legend className="mt-form-field__label">{ui('freeMealDayLabel')}</legend>
                <div className="mt-segment mt-segment--days">
                  {DAY_SHORT_KEYS.map((shortKey, index) => (
                    <button key={shortKey} type="button" aria-pressed={freeMealDay === index} aria-label={diet(DAY_KEYS[index])} onClick={() => setFreeMealDay(index)}>
                      {diet(shortKey)}
                    </button>
                  ))}
                </div>
              </fieldset>
              <fieldset className="mt-diet-free__group">
                <legend className="mt-form-field__label">{ui('freeMealSlotLabel')}</legend>
                <div className="mt-segment mt-diet-free-slot">
                  {FREE_MEAL_SLOTS.map((slot) => (
                    <button key={slot} type="button" aria-pressed={freeMealSlot === slot} onClick={() => setFreeMealSlot(slot)}>
                      {diet(`mealSlots.${slot}`)}
                    </button>
                  ))}
                </div>
              </fieldset>
              <p className="mt-diet-free__summary">
                <Star aria-hidden="true" />
                {ui('freeMealSummary', { day: diet(DAY_KEYS[freeMealDay]), slot: diet(`mealSlots.${freeMealSlot}`).toLowerCase() })}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // The foods to leave out: by category or by search, with what's left out always in view
  const renderExclusions = () => {
    const isSearching = searchExclusions.length > 0;
    const activeCategory = selectedCategory || foodExclusionCategoriesToUse[0]?.name;
    const visibleFoods = isSearching
      ? foodExclusionCategoriesToUse.flatMap((category) =>
          category.foods.filter((food) => food.toLowerCase().includes(searchExclusions.toLowerCase()))
        )
      : foodExclusionCategoriesToUse.find((category) => category.name === activeCategory)?.foods || [];

    return (
      <div className="mt-diet-form">
        <p className="mt-diet-wizard__lead">{diet('foodExclusionsDesc')}</p>
        <div className="mt-diet-field">
          <label className="mt-search mt-diet-search">
            <Search aria-hidden="true" />
            <span className="mt-sr-only">{diet('searchFoods')}</span>
            <input type="search" value={searchExclusions} onChange={(event) => setSearchExclusions(event.target.value)} placeholder={diet('searchFoods')} />
          </label>
          {!isSearching && (
            <div className="mt-chip-row mt-chip-row--wrap" role="group" aria-label={ui('foodCategories')}>
              {foodExclusionCategoriesToUse.map((category) => (
                <button
                  key={category.name}
                  type="button"
                  className="mt-filter-chip"
                  aria-pressed={activeCategory === category.name}
                  onClick={() => setSelectedCategory(category.name)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}
          <ul className="mt-food-toggles">
            {visibleFoods.map((food) => (
              <li key={food}>
                <button type="button" className="mt-food-toggle" aria-pressed={excludedFoods.includes(food)} onClick={() => toggleFoodExclusion(food)}>
                  {food}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <section className="mt-diet-excluded" aria-live="polite">
          <p className="mt-diet-excluded__head">
            <span>{excludedFoods.length > 0 ? ui('excludedCount', { count: excludedFoods.length }) : ui('excludedNone')}</span>
            {excludedFoods.length > 0 && (
              <button type="button" className="mt-text-btn" onClick={() => setExcludedFoods([])}>
                {diet('clearAll')}
              </button>
            )}
          </p>
          {excludedFoods.length > 0 && (
            <ul className="mt-diet-excluded__list">
              {excludedFoods.map((food) => (
                <li key={food}>
                  <button type="button" className="mt-diet-excluded__chip" onClick={() => toggleFoodExclusion(food)} aria-label={ui('excludedRemove', { food })}>
                    {food}
                    <X aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    );
  };

  // The summary's answers, each with the way to change it
  const renderAnswers = () => {
    const range = useImperial ? RANGES.lbs : RANGES.kg;
    const unit = useImperial ? diet('lbs') : diet('kg');
    const isWeightOff = weightDraft !== null && weightDraft !== '' && !inRange(weightDraft, range);
    const lower = (text) => (language === 'pt' ? text.toLowerCase() : text);
    const heightText = useImperial ? `${heightFt}′${heightIn || 0}″` : `${height} cm`;
    const activity = activityOptions.find((option) => option.key === activityLevel)?.label;
    const freeMeal =
      freeMealDay === null
        ? ui('summaryNoFreeMeal')
        : ui('summaryFreeMeal', { day: lower(diet(DAY_KEYS[freeMealDay])), slot: lower(diet(`mealSlots.${freeMealSlot}`)) });
    const shownFoods = excludedFoods.slice(0, 4).join(', ') + (excludedFoods.length > 4 ? ` +${excludedFoods.length - 4}` : '');
    const rows = [
      { key: 'you', step: STEP.info, label: ui('summaryYou'), value: [diet(gender === 'male' ? 'male' : 'female'), ui('summaryAge', { age }), heightText, activity].filter(Boolean).join(' · ') },
      { key: 'weight' },
      { key: 'goal', step: STEP.goal, label: ui('summaryGoal'), value: goalLabel },
      { key: 'meals', step: STEP.meals, label: ui('summaryMeals'), value: `${ui('mealsCountOption', { count: mealsPerDay })} · ${freeMeal}` },
      {
        key: 'foods',
        step: STEP.foods,
        label: ui('summaryFoods'),
        value: excludedFoods.length > 0 ? ui('summaryExcluded', { foods: shownFoods }) : ui('summaryEatsAll'),
      },
    ];

    return (
      <section className="mt-diet-card mt-diet-answers col-span-full" aria-labelledby="mt-diet-answers-title">
        <h2 id="mt-diet-answers-title" className="mt-diet-card__title">
          {ui('summaryTitle')}
        </h2>
        <dl className="mt-diet-answers__list">
          {rows.map((row) =>
            row.key === 'weight' ? (
              // The weight changes most between plans: it's edited right here, and the numbers above follow it
              <div key="weight" className="mt-diet-answers__row">
                <dt>
                  <label htmlFor="mt-diet-answers-weight">{diet('weight')}</label>
                </dt>
                <dd>
                  <span className="mt-diet-answers__weight">
                    <input
                      id="mt-diet-answers-weight"
                      type="number"
                      inputMode="decimal"
                      min={range[0]}
                      max={range[1]}
                      value={weightDraft ?? weight}
                      onChange={(event) => {
                        setWeightDraft(event.target.value);
                        if (inRange(event.target.value, range)) setWeight(event.target.value);
                      }}
                      onBlur={() => setWeightDraft(null)}
                      aria-invalid={isWeightOff || undefined}
                      aria-describedby={isWeightOff ? 'mt-diet-answers-weight-error' : undefined}
                    />
                    {unit}
                  </span>
                  {isWeightOff && (
                    <span id="mt-diet-answers-weight-error" className="mt-form-field__error">
                      {ui('weightRange', { min: range[0], max: range[1], unit })}
                    </span>
                  )}
                </dd>
              </div>
            ) : (
              <div key={row.key} className="mt-diet-answers__row">
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
                <button
                  type="button"
                  className="mt-text-btn"
                  onClick={() => editSection(row.step)}
                  aria-label={ui('editSection', { section: lower(row.label) })}
                >
                  {ui('edit')}
                </button>
              </div>
            )
          )}
        </dl>
      </section>
    );
  };

  const renderResults = () => {
    if (!nutritionData) return null;
    // What the target means, in plain words: what the body burns, how far the target is from it, and what that
    // does to the weight in a week (7,700 kcal ≈ 1 kg)
    const difference = Math.abs(Math.round(nutritionData.tdee - nutritionData.targetCalories));
    const weeklyKg = (difference * 7) / 7700;
    const pace = (useImperial ? weeklyKg * 2.20462 : weeklyKg).toLocaleString(language === 'pt' ? 'pt-BR' : 'en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    const explainKey = goal === 'lose' ? 'targetExplainLose' : goal === 'gain' ? 'targetExplainGain' : 'targetExplainMaintain';
    return (
      <div className="mt-diet-results">
        <section className="mt-diet-target">
          <p className="mt-diet-target__label">{diet('dailyTarget')}</p>
          <p className="mt-diet-target__value">
            {nutritionData.targetCalories.toLocaleString(language === 'pt' ? 'pt-BR' : 'en-US')} <small>{diet('kcalDay')}</small>
          </p>
          <p className="mt-diet-target__explain">
            {ui(explainKey, {
              tdee: formatKcal(nutritionData.tdee),
              difference: formatKcal(difference),
              pace,
              unit: useImperial ? diet('lbs') : diet('kg'),
            })}
          </p>
        </section>
        <section className="mt-diet-card">
          <h2 className="mt-diet-card__title">{diet('dailyMacros')}</h2>
          {macroRows(nutritionData.macros, { asRows: true })}
        </section>
        {renderAnswers()}
        {/* The sign-up consent row's look, across both result columns on desktop */}
        <label className="mt-auth__terms col-span-full">
          <input
            type="checkbox"
            required
            checked={hasHealthConsent}
            aria-invalid={isConsentMissing || undefined}
            onChange={(event) => {
              setHasHealthConsent(event.target.checked);
              setIsConsentMissing(false);
            }}
          />
          <span>
            {diet('healthConsent')}{' '}
            <a href={t('privacyUrl')} target="_blank" rel="noopener noreferrer">
              {t('standalone.privacyLinkText')}
            </a>
            .
          </span>
        </label>
      </div>
    );
  };

  // ── Plan (step 5) ─────────────────────────────────────────────────────

  // Pro, a planned meal (not the free one), and no week being built underneath it: its foods can be changed by hand
  const canSwap = (meal) => isPro && !meal.isFreeMeal && !isGenerating;
  // The AI's meal swap only on the AI's plans: an imported or hand-built plan keeps its own meals (foods still change)
  const canSwapMeal = (meal) => canSwap(meal) && !isOwnPlan(origin);
  const isSwappingMeal = (index) => swapping?.day === selectedDay && swapping?.meal === index;
  const isSwappedMeal = (index) => lastSwap?.day === selectedDay && lastSwap?.meal === index;
  const mealName = (meal) => (language === 'pt' ? sentenceCase(meal.name) : meal.name);
  const foodNames = (meal) => new Intl.ListFormat(locale, { type: 'conjunction' }).format(mainFoods(meal.foods, { lowerRest: language === 'pt' }));

  // Desktop opens the day's first meal, unless it's the next one (its foods are on its card); phones open none
  const openMeals = expandedMeals ?? (isDesktopView() && nextMeal !== 0 ? new Set([0]) : new Set());
  const toggleMealExpanded = (idx) => {
    const wasExpanded = openMeals.has(idx);
    trackEvent(
      `diet_plan_${wasExpanded ? 'collapse' : 'expand'}_meal`,
      `Diet Plan - ${wasExpanded ? 'Collapse' : 'Expand'} Meal`,
      'diet_plan',
      true
    );
    const next = new Set(openMeals);
    if (wasExpanded) {
      next.delete(idx);
    } else {
      next.add(idx);
    }
    setExpandedMeals(next);
  };

  const toggleWeek = () => {
    trackEvent(
      showWeekOverview ? 'diet_plan_back_to_day_view' : 'diet_plan_toggle_week_view',
      showWeekOverview ? 'Diet Plan - Back to Day View' : 'Diet Plan - Toggle Week View',
      'diet_plan',
      true
    );
    setShowWeekOverview(!showWeekOverview);
  };

  const swapButton = (meal, index) =>
    canSwapMeal(meal) && (
      <button
        type="button"
        className="mt-btn mt-btn--quiet"
        onClick={() => swapMeal(selectedDay, index)}
        disabled={!!swapping}
        aria-busy={isSwappingMeal(index) || undefined}
      >
        <RefreshCw className={isSwappingMeal(index) ? 'mt-spin' : undefined} aria-hidden="true" />
        {ui(isSwappingMeal(index) ? 'swapping' : 'swapMeal')}
      </button>
    );

  // A food of a meal that can be changed (canSwap): in the table its name and amount are the button that opens the
  // sheet, a pencil after the amount. No column of its own, so the table keeps its width on phones
  const foodCell = (meal, mealIndex, food, foodIndex) => {
    const quantity = formatQuantity(food.quantity);
    if (!canSwap(meal)) {
      return (
        <>
          {food.name} <small>{quantity}</small>
        </>
      );
    }
    return (
      <button
        type="button"
        className="mt-dp-food-edit"
        onClick={() => openFoodEdit(selectedDay, mealIndex, foodIndex)}
        aria-label={ui('editFood', { food: food.name, quantity })}
      >
        <span className="mt-dp-food-edit__name">{food.name}</span>{' '}
        <small>
          {quantity}
          <Pencil className="mt-dp-pencil" aria-hidden="true" />
        </small>
      </button>
    );
  };

  // A meal with options ("opção 1 / opção 2"): the day's is picked here, and it's what the tracker logs
  const pickOption = (mealIndex, optionIndex) => {
    const meal = weekPlanRef.current[selectedDay].meals[mealIndex];
    if ((meal.optionIndex ?? 0) === optionIndex) return;
    trackEvent('diet_plan_option_pick', 'Diet Plan - Pick Option', 'diet_plan', true);
    replaceMeal(selectedDay, mealIndex, chooseOption(meal, optionIndex));
  };

  const optionSwitch = (meal, mealIndex) =>
    meal.options?.length > 1 && (
      <div className="mt-dp-opts" role="group" aria-label={t('pages.DietImport.optionsLabel')}>
        {meal.options.map((option, optionIndex) => (
          <button
            key={optionIndex}
            type="button"
            aria-pressed={(meal.optionIndex ?? 0) === optionIndex}
            onClick={() => pickOption(mealIndex, optionIndex)}
            disabled={!isPro || isGenerating}
          >
            {t('pages.DietImport.optionN', { n: optionIndex + 1 })}
            <small>{formatKcal(mealTotals({ foods: option.foods }).calories)} kcal</small>
          </button>
        ))}
      </div>
    );

  // "Pontos para a consulta" for plans from outside the AI: on desktop in the plate's card, on phones after the meals
  const pointsEntry = (place) =>
    isOwnPlan(origin) &&
    isWeekComplete && (
      <button type="button" className={`mt-dp-points mt-dp-points--${place}`} onClick={openPoints}>
        <PreviewArt kind="sheet" badge="question" size="sm" />
        <span className="mt-dp-points__text">
          <span className="mt-dp-points__title">{t('pages.DietImport.pointsTitle')}</span>
          <span className="mt-dp-points__sub">{t('pages.DietImport.pointsEntrySub')}</span>
        </span>
        <ChevronRight className="mt-dp-points__chevron" aria-hidden="true" />
      </button>
    );

  const undoButton = (index) =>
    isSwappedMeal(index) && (
      <button type="button" className="mt-dp-link mt-dp-link--ink" onClick={undoSwap}>
        <RotateCcw aria-hidden="true" />
        {ui('undoSwap')}
      </button>
    );

  // A swap the AI limit turned down, said under the meal's buttons
  const swapLimitNote = (index) =>
    swapLimit?.day === selectedDay &&
    swapLimit?.meal === index && (
      <p className="mt-dp-note" role="alert">
        <CircleAlert aria-hidden="true" />
        {aiLimitText(t, swapLimit.limit, { locale }).message}
      </p>
    );

  // Protein, carbs and fat: a ring with each one's share of the calories, then its grams
  const macroRings = (totals) => (
    <ul className="mt-dp-macros">
      {macroShares(totals).map(({ key, grams, share, pct }) => {
        const Icon = MACRO_ICONS[key];
        return (
          <li key={key}>
            <ProgressRing value={share} size={62} stroke={6} className="mt-dp-macros__ring">
              <Icon className="mt-dp-macros__icon" aria-hidden="true" />
            </ProgressRing>
            <span className="mt-dp-macros__name">{diet(key)}</span>
            <span className="mt-dp-macros__grams">
              {Math.round(grams)} {diet('grams')} <span>· {pct}%</span>
            </span>
          </li>
        );
      })}
    </ul>
  );

  // The day as a plate: one arc per meal, green once it's in the tracker; in the middle, what the tracker holds
  const renderDaySide = () => {
    const { mealsWithTotals, dayTotals: totals } = selectedDayData;
    const target = nutritionData?.targetCalories;
    const segments = mealsWithTotals.map((meal, index) => ({
      key: `${index}-${meal.name}`,
      kcal: meal.totals.calories,
      state: canLogDay && isMealLogged(index) ? 'logged' : index === nextMeal ? 'next' : 'idle',
      icon: <MealIcon meal={meal} />,
      isFree: meal.isFreeMeal,
    }));
    return (
      <>
        <section className="mt-dp-hero" aria-label={ui('daySummary')}>
          <MealPlate segments={segments} size={250} stroke={26} className="mt-dp-hero__ring">
            <span className="mt-dp-hero__mid">
              <b>{formatKcal(shownDayKcal)}</b>
              {canLogDay ? (
                <>
                  <small>{target ? ui('plateOf', { kcal: formatKcal(target) }) : diet('calories')}</small>
                  <em>{ui('plateTracked')}</em>
                </>
              ) : (
                <>
                  <small>{ui('platePlanned')}</small>
                  <em>{ui('plateNotYet', { day: language === 'pt' ? dayLabel(selectedDay).toLowerCase() : dayLabel(selectedDay) })}</em>
                </>
              )}
            </span>
          </MealPlate>
          <p className="mt-dp-hero__line">
            {ui('heroPlan', { kcal: formatKcal(totals.calories) })}
            {target && <span> · {ui('heroTarget', { kcal: formatKcal(target) })}</span>}
          </p>
          {canLogDay && (
            <Link className="mt-dp-link" to={trackerLink()}>
              {ui('openTracker')}
            </Link>
          )}
        </section>
        {macroRings(totals)}
      </>
    );
  };

  // The meal to eat now, ready to log: today's first meal with nothing in the tracker
  const renderNextMeal = () => {
    if (nextMeal < 0) return null;
    const meal = selectedDayData.mealsWithTotals[nextMeal];
    return (
      <section className="mt-dp-next" aria-label={ui('nextLabel')}>
        <div className="mt-dp-next__head">
          <span className="mt-dp-disc" aria-hidden="true">
            <MealIcon meal={meal} />
          </span>
          <div>
            <h2 className="mt-dp-next__title">
              {mealName(meal)} <span className="mt-dp-pill">{ui('nextTag')}</span>
            </h2>
            <p className="mt-dp-next__sub">
              {meal.isFreeMeal
                ? meal.budget
                  ? ui('freeMealNextSub', { kcal: formatKcal(meal.budget) })
                  : diet('freeMeal')
                : ui('nextSub', { kcal: formatKcal(meal.totals.calories), foods: ui('foodsCount', { count: meal.foods.length }) })}
            </p>
          </div>
        </div>
        {optionSwitch(meal, nextMeal)}
        {meal.isFreeMeal ? (
          <p className="mt-dp-next__free">{meal.budget ? ui('freeMealNextText', { kcal: formatKcal(meal.budget) }) : ui('freeMealTextLegacy')}</p>
        ) : (
          // A swapped meal's new foods settle in (the key changes with them)
          <ul key={meal.foods.map((food) => food.name).join('|')} className={`mt-dp-foods${isSwappedMeal(nextMeal) ? ' is-swapped' : ''}`}>
            {meal.foods.map((food, foodIndex) => {
              const quantity = formatQuantity(food.quantity);
              const isEditable = canSwap(meal);
              const row = (
                <>
                  <span className="mt-dp-foods__name">
                    <span>{food.name}</span>{' '}
                    <small>
                      {quantity}
                      {isEditable && <Pencil className="mt-dp-pencil" aria-hidden="true" />}
                    </small>
                  </span>
                  <span className="mt-dp-foods__kcal">
                    {formatKcal(food.calories)} {diet('calories')}
                  </span>
                  <span className="mt-dp-foods__macros">
                    {ui('foodMacros', { protein: Math.round(food.protein || 0), carbs: Math.round(food.carbs || 0), fat: Math.round(food.fat || 0) })}
                  </span>
                </>
              );
              // On the card the whole row opens the food's sheet: a bigger target than the name alone
              return (
                <li key={`${foodIndex}-${food.name}`}>
                  {isEditable ? (
                    <button
                      type="button"
                      className="mt-dp-foods__edit"
                      onClick={() => openFoodEdit(selectedDay, nextMeal, foodIndex)}
                      aria-label={ui('editFood', { food: food.name, quantity })}
                    >
                      {row}
                    </button>
                  ) : (
                    row
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-dp-next__actions">
          {meal.isFreeMeal ? (
            <Link className="mt-btn mt-btn--primary" to={trackerLink(nextMeal, true)}>
              <Plus aria-hidden="true" />
              {ui('logFreeMeal')}
            </Link>
          ) : (
            <button type="button" className="mt-btn mt-btn--primary" onClick={() => logPlannedMeal(meal, nextMeal)} disabled={calorieLog.isSaving}>
              <Check aria-hidden="true" />
              {ui('logMeal')}
            </button>
          )}
          {swapButton(meal, nextMeal)}
          {undoButton(nextMeal)}
          {swapLimitNote(nextMeal)}
        </div>
      </section>
    );
  };

  // One meal on the day's line: ✓ logged, ● next, ★ free; it opens to its foods and what can be done with it
  const renderMeal = (meal, index) => {
    const isOpen = openMeals.has(index);
    const panelId = `diet-meal-${index}`;
    const isLogged = canLogDay && isMealLogged(index);
    const isNext = index === nextMeal;
    const nodeClass = [meal.isFreeMeal && 'is-free', isLogged && 'is-done', !isLogged && isNext && 'is-next'].filter(Boolean).join(' ');
    let sub = foodNames(meal);
    if (meal.options?.length > 1) sub = t('pages.DietImport.mealOptionsSub', { count: meal.options.length, n: (meal.optionIndex ?? 0) + 1 });
    if (meal.time) sub = `${meal.time} · ${sub}`;
    if (isLogged) sub = ui('mealLogged', { kcal: formatKcal(loggedKcal(index)) });
    else if (meal.isFreeMeal) sub = diet('freeMeal');
    else if (isNext) sub = ui('nextTag');
    return (
      <li key={`${index}-${meal.name}`} className="mt-dp-meal">
        <button type="button" className="mt-dp-meal__head" aria-expanded={isOpen} aria-controls={panelId} onClick={() => toggleMealExpanded(index)}>
          <span className={`mt-dp-node ${nodeClass}`} aria-hidden="true">
            {meal.isFreeMeal ? <Star /> : isLogged ? <Check /> : null}
          </span>
          <span className="mt-dp-meal__text">
            <span className="mt-dp-meal__name">{mealName(meal)}</span>
            <span className={`mt-dp-meal__sub${isLogged ? ' is-done' : ''}`}>{sub}</span>
          </span>
          {/* A free meal saved before the budget has no calories to show */}
          {(!meal.isFreeMeal || meal.budget) && (
            <span className={`mt-dp-meal__kcal${isLogged ? ' is-muted' : ''}`}>
              {meal.isFreeMeal ? ui('freeMealBudget', { kcal: formatKcal(meal.budget) }) : `${formatKcal(meal.totals.calories)} ${diet('calories')}`}
            </span>
          )}
          <ChevronDown className="mt-dp-meal__chevron" aria-hidden="true" />
        </button>
        {isOpen && (
          <div id={panelId} className="mt-dp-meal__body">
            {optionSwitch(meal, index)}
            {meal.isFreeMeal && meal.foods.length === 0 ? (
              <p className="mt-dp-meal__free">{meal.budget ? ui('freeMealText', { kcal: formatKcal(meal.budget) }) : ui('freeMealTextLegacy')}</p>
            ) : (
              <table className={`mt-dp-table${isSwappedMeal(index) ? ' is-swapped' : ''}`}>
                <thead>
                  <tr>
                    <th scope="col">{diet('food')}</th>
                    <th scope="col">{diet('calories')}</th>
                    <th scope="col" title={diet('protein')}>
                      {ui('proteinShort')}
                    </th>
                    <th scope="col" title={diet('carbs')}>
                      {ui('carbsShort')}
                    </th>
                    <th scope="col" title={diet('fat')}>
                      {ui('fatShort')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {meal.foods.map((food, foodIndex) => (
                    <tr key={`${foodIndex}-${food.name}`}>
                      <th scope="row">{foodCell(meal, index, food, foodIndex)}</th>
                      <td>{Math.round(food.calories)}</td>
                      <td>{Math.round(food.protein)}</td>
                      <td>{Math.round(food.carbs)}</td>
                      <td>{Math.round(food.fat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(canLogDay || canSwap(meal)) && (
              <div className="mt-dp-meal__actions">
                {!canLogDay ? null : isLogged ? (
                  <Link className="mt-dp-link" to={trackerLink(index)}>
                    {ui('viewInTracker')}
                  </Link>
                ) : meal.isFreeMeal ? (
                  <Link className="mt-btn mt-btn--quiet" to={trackerLink(index, true)}>
                    <Plus aria-hidden="true" />
                    {ui('logFreeMeal')}
                  </Link>
                ) : (
                  <button type="button" className="mt-btn mt-btn--quiet" onClick={() => logPlannedMeal(meal, index)} disabled={calorieLog.isSaving}>
                    <Plus aria-hidden="true" />
                    {ui('logMeal')}
                  </button>
                )}
                {swapButton(meal, index)}
                {undoButton(index)}
                {swapLimitNote(index)}
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  const renderDayMain = () => {
    const isToday = selectedDay === todayIndex;
    return (
      <>
        {renderNextMeal()}
        <div className="mt-dp-section">
          <h2 className="mt-dp-section__title">
            {isToday ? ui('yourDay') : dayTitle(selectedDay)}
            {!isToday && hasFreeMeal(selectedDayData) && <Star className="mt-dp-star" aria-label={diet('freeMeal')} />}
          </h2>
          <span className="mt-dp-section__aside">{ui('dayPlanned', { kcal: formatKcal(selectedDayData.dayTotals.calories) })}</span>
        </div>
        <ol className="mt-dp-meals">{selectedDayData.mealsWithTotals.map(renderMeal)}</ol>
      </>
    );
  };

  // The week's average against the target, in the day's place
  const renderWeekSide = () => (
    <>
      <section className="mt-dp-hero" aria-label={ui('average')}>
        <ProgressRing value={nutritionData ? weeklyAverages.calories / nutritionData.targetCalories : 0} size={250} stroke={26} className="mt-dp-hero__ring mt-dp-hero__ring--week">
          <span className="mt-dp-hero__mid">
            <b>{formatKcal(shownWeekKcal)}</b>
            <small>{ui('averageUnit')}</small>
            <em>{ui('averageSub')}</em>
          </span>
        </ProgressRing>
        <p className="mt-dp-hero__line">
          {nutritionData ? ui('targetLine', { kcal: formatKcal(nutritionData.targetCalories) }) : null}
          <span> · {ui('weekDays', { count: readyDays })}</span>
        </p>
      </section>
      {macroRings(weeklyAverages)}
    </>
  );

  // The seven days, each with its plate in small (green: logged meals); a tap opens the day
  const renderWeekMain = () => (
    <>
      <div className="mt-dp-section">
        <h2 className="mt-dp-section__title">{ui('yourWeek')}</h2>
        <span className="mt-dp-section__aside">{ui('weekHint')}</span>
      </div>
      <ul className="mt-dp-week">
        {dayTotals.map((day, index) => {
          const canLog = index <= todayIndex;
          const loggedMeals = canLog ? new Set(entriesOf(index).map((entry) => entry.mealIndex ?? 0)) : new Set();
          const isFree = hasFreeMeal(day);
          const segments = day.meals.map((meal, mealIndex) => ({
            key: `${mealIndex}-${meal.name}`,
            kcal: mealTotals(meal).calories,
            state: loggedMeals.has(mealIndex) ? 'logged' : 'idle',
          }));
          let sub = ui(isFree ? 'mealsCountFree' : 'mealsCount', { count: day.meals.length });
          if (canLog) sub = ui('loggedOf', { done: day.meals.filter((_, mealIndex) => loggedMeals.has(mealIndex)).length, count: day.meals.length });
          return (
            <li key={DAY_KEYS[index]}>
              <button type="button" className="mt-dp-week__day" onClick={() => showDay(index, { scrollUp: true })}>
                <MealPlate segments={segments} size={52} stroke={6} gap={0.03} className="mt-dp-week__plate">
                  <span className="mt-dp-week__short">{diet(DAY_SHORT_KEYS[index])}</span>
                </MealPlate>
                <span className="mt-dp-week__text">
                  <span className="mt-dp-week__title">
                    <span className="mt-dp-week__long">{dayTitle(index)}</span>
                    <span className="mt-dp-week__compact">{dayTitleShort(index)}</span>
                    {isFree && <Star className="mt-dp-star" aria-label={diet('freeMeal')} />}
                    {index === todayIndex && (
                      <>
                        {' '}
                        <span className="mt-dp-pill mt-dp-pill--sm">{ui('todayTag')}</span>
                      </>
                    )}
                  </span>
                  <span className="mt-dp-week__sub">{sub}</span>
                </span>
                <span className="mt-dp-week__kcal">
                  {formatKcal(day.totals.calories)} {diet('calories')}
                </span>
                <ChevronRight className="mt-dp-week__chevron" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {/* Phones: the head holds only icons, so the week's own actions sit under it */}
      <div className="mt-dp-week__actions">
        <button type="button" className="mt-btn mt-btn--quiet" onClick={handleExportPDF} disabled={isExportingPDF}>
          <Download aria-hidden="true" />
          {ui('exportPdf')}
        </button>
        {!isGenerating && (
          <button type="button" className="mt-btn mt-btn--quiet" onClick={changePlan}>
            <SlidersHorizontal aria-hidden="true" />
            {ui('changeLink')}
          </button>
        )}
      </div>
    </>
  );

  const planHead = (
    <div className="mt-dp-head">
      <div>
        <h1 className="mt-dp-head__title">{ui('title')}</h1>
        {nutritionData && (
          <p className="mt-dp-head__sub">
            {ui('planSub', { kcal: formatKcal(nutritionData.targetCalories), goal: isOwnPlan(origin) ? t(`pages.DietImport.planFrom_${origin}`) : goalLabel })}
          </p>
        )}
      </div>
      {isWeekComplete && (
        // Phones: the week and the PDF as round buttons beside the title
        <div className="mt-dp-head__icons">
          <button type="button" className="mt-dp-icon-btn" aria-pressed={showWeekOverview} aria-label={ui('weekLink')} onClick={toggleWeek}>
            <CalendarDays aria-hidden="true" />
          </button>
          <button type="button" className="mt-dp-icon-btn" onClick={handleExportPDF} disabled={isExportingPDF} aria-label={ui('exportPdf')}>
            <Download aria-hidden="true" />
          </button>
        </div>
      )}
      <div className="mt-dp-head__actions">
        {isWeekComplete && (
          <>
            <button type="button" className="mt-btn mt-btn--quiet" onClick={toggleWeek}>
              {showWeekOverview ? <ChevronLeft aria-hidden="true" /> : <CalendarDays aria-hidden="true" />}
              {ui(showWeekOverview ? 'backToDay' : 'weekLink')}
            </button>
            <button type="button" className="mt-btn mt-btn--quiet" onClick={handleExportPDF} disabled={isExportingPDF}>
              <Download aria-hidden="true" />
              {ui('exportPdf')}
            </button>
          </>
        )}
        {!isGenerating && (
          <button type="button" className="mt-btn mt-btn--quiet" onClick={changePlan}>
            <SlidersHorizontal aria-hidden="true" />
            {ui('changeLink')}
          </button>
        )}
      </div>
    </div>
  );

  // What a kept plan shows under its lock: the plan page's frame with bars where the meals would be, never the plan
  const renderKeptStandIn = () => (
    <div className="mt-dp">
      <div className="mt-dp-head">
        <div>
          <h1 className="mt-dp-head__title">{ui('title')}</h1>
          {nutritionData && <p className="mt-dp-head__sub">{ui('planSub', { kcal: formatKcal(nutritionData.targetCalories), goal: goalLabel })}</p>}
        </div>
      </div>
      <div className="mt-dp-layout">
        <div className="mt-dp-side">
          <div className="mt-dp-hero">
            <ProgressRing value={0} size={250} stroke={26} track="gray" className="mt-dp-hero__ring" />
          </div>
          <ul className="mt-dp-macros">
            {['protein', 'carbs', 'fat'].map((key) => (
              <li key={key}>
                <ProgressRing value={0} size={62} stroke={6} track="gray" className="mt-dp-macros__ring" />
                <span className="mt-dp-bar mt-dp-bar--short" />
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-dp-main">
          <div className="mt-dp-next mt-dp-standin">
            {[60, 100, 100, 100].map((width, index) => (
              <span key={index} className="mt-dp-bar" style={{ width: `${width}%` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ── Page ──────────────────────────────────────────────────────────────

  // The plan shows on its step, and on /diet itself once there is one; without one, /diet offers the ways to get one
  const isPlan = !view && !!weekPlan && (currentStep === STEP.plan || !searchParams.has('step'));
  const showEntry = !isGenerating && (view === 'start' || (!view && !searchParams.has('step') && !weekPlan));
  let isWizard = false;
  // A plan kept from a Pro subscription that has ended: never deleted, and shown locked behind the way back to Pro.
  // The API sends it without its week of meals, so what's under the lock is a stand-in, never the plan
  const isLocked = !isPro && !!dataMe && !!dataDietPlan?.dietPlan?.data;
  // Free accounts answer the questionnaire and see their numbers; only the AI week needs Pro
  const needsPro = !isPro && !!dataMe;
  const wizardTitles = [diet('stepPersonalInfo'), diet('goal'), ui('mealsTitle'), diet('foodExclusions'), ui('results')];

  let content;
  if (isLoadingDietPlan) {
    content = (
      <div className="mt-builder__status" aria-busy="true">
        <span className="mt-builder__spinner" aria-hidden="true" />
      </div>
    );
  } else if (view === 'import' || view === 'manual') {
    content = (
      <DietCreate
        key={view}
        mode={view}
        isPro={isPro}
        isProKnown={!!dataMe}
        hasPlan={hasSavedPlan}
        onBack={backFromView}
        onSave={saveOwnPlan}
        onUpgrade={() => {
          trackEvent('diet_plan_upgrade_modal', 'Diet Plan - Upgrade Modal Open', 'diet_plan', true);
          onUpgradeOpen();
        }}
        onDirtyChange={setIsDraftDirty}
      />
    );
  } else if (view === 'points') {
    content = weekPlan ? (
      <DietPoints
        planId={savedPlanIdRef.current}
        weekPlan={weekPlan}
        origin={origin}
        originalWeekPlan={originalWeekPlan}
        substitutions={planSubstitutions}
        questionnaire={gender ? { gender, age, weight, height, heightFt, heightIn, useImperial, activityLevel, goal } : null}
        onBack={backFromView}
        onChangeWeek={changeWeek}
      />
    ) : (
      <div className="mt-builder__status" aria-busy="true">
        <span className="mt-builder__spinner" aria-hidden="true" />
      </div>
    );
  } else if (showEntry) {
    content = (
      <DietStart
        hasPlan={hasSavedPlan}
        isPro={isPro || !dataMe}
        onAi={startWithAi}
        onImport={startImport}
        onManual={startManual}
        onBack={backToPlanView}
      />
    );
  } else if (isGenerating && !weekPlan?.length) {
    // Until the first day arrives; then the plan opens and the rest are built while it's read
    content = (
      <section className="mt-diet-generating" role="status">
        <span className="mt-builder__spinner" aria-hidden="true" />
        <h2 className="mt-builder__title">{diet('creatingMealPlan')}</h2>
        <p className="mt-builder__sub">{diet('creatingSubtitle')}</p>
        <ol className="mt-gen-days" aria-hidden="true">
          {DAY_SHORT_KEYS.map((key, index) => (
            <li key={key} className={index < generationProgress ? 'is-done' : index === generationProgress ? 'is-active' : ''}>
              <span>{index < generationProgress ? <Check /> : null}</span>
              {diet(key)}
            </li>
          ))}
        </ol>
        <p className="mt-builder__sub">{diet('generatingDay', { current: Math.min(generationProgress + 1, 7), total: 7 })}</p>
        <span className="mt-progress mt-builder__progress">
          <span style={{ width: `${(generationProgress / 7) * 100}%` }} />
        </span>
      </section>
    );
  } else if (isPlan) {
    // Phones: the head, the status, the days, the day's plate and the meals in one column. Desktop: the plate and the
    // days in a card on the left, the meals on the right
    content = (
      <div className="mt-dp">
        {planHead}
        {renderBuilding()}
        {selectedDayData && (
          <div className="mt-dp-layout">
            <div className="mt-dp-side">
              {showWeekOverview && weeklyAverages ? renderWeekSide() : renderDaySide()}
              {weekRings}
              {pointsEntry('side')}
            </div>
            <div className="mt-dp-main">
              {showWeekOverview ? renderWeekMain() : renderDayMain()}
              {pointsEntry('main')}
            </div>
          </div>
        )}
        {!showWeekOverview && !isGenerating && (
          <div className="mt-dp-foot">
            <button type="button" className="mt-dp-link" onClick={changePlan}>
              <SlidersHorizontal aria-hidden="true" />
              {ui('changeLink')}
            </button>
          </div>
        )}
      </div>
    );
  } else {
    isWizard = true;
    const isResults = currentStep === STEP.results;
    content = (
      <>
        <div className="mt-page-head">
          <div>
            <h1 className="mt-page-head__title">{ui('title')}</h1>
            <p className="mt-page-head__sub">{ui('wizardSub')}</p>
          </div>
        </div>
        <div className="mt-diet-wizard">
          <div className="mt-builder__steps mt-diet-steps">
            <span className="mt-builder__segments mt-diet-steps__segments" aria-hidden="true">
              {wizardTitles.map((title, index) => (
                <span key={title} className={index <= currentStep ? 'is-done' : ''} />
              ))}
            </span>
            <span className="mt-builder__step-label">{ui('step', { n: currentStep + 1, total: WIZARD_STEPS })}</span>
          </div>
          <h2 className="mt-builder__title mt-diet-wizard__title">{wizardTitles[currentStep]}</h2>
          {currentStep === STEP.info && renderPersonalInfo()}
          {currentStep === STEP.goal && renderGoal()}
          {currentStep === STEP.meals && renderMeals()}
          {currentStep === STEP.foods && renderExclusions()}
          {isResults && renderResults()}
          {/* The plan's first day turned down by an AI limit: said here, by the button that asked for it */}
          {isResults && generationLimit && failedDay === null && (
            <p className="mt-dp-note mt-diet-limit" role="alert">
              <CircleAlert aria-hidden="true" />
              {aiLimitText(t, generationLimit, { locale }).message}
            </p>
          )}
        </div>
        <div className="mt-editor-savebar mt-diet-actions">
          {isResults && planBeforeChange ? (
            <button type="button" className="mt-btn mt-btn--quiet" onClick={backToPlan}>
              {ui('backToPlan')}
            </button>
          ) : currentStep === STEP.info && !isEditing ? (
            // The questionnaire's first step goes back to the ways of getting a plan
            <button type="button" className="mt-btn mt-btn--quiet" onClick={() => navigate('/diet?view=start', { replace: true })}>
              {diet('back')}
            </button>
          ) : (
            currentStep > 0 &&
            !isEditing && (
              <button type="button" className="mt-btn mt-btn--quiet" onClick={handleBack}>
                {diet('back')}
              </button>
            )
          )}
          {isResults ? (
            <button type="button" className="mt-btn mt-btn--primary" onClick={startGeneration} disabled={isGenerating || !nutritionData}>
              {diet('generateMealPlan')}
              {needsPro && (
                <span className="mt-btn__pro">
                  <Crown aria-hidden="true" />
                  Pro
                </span>
              )}
            </button>
          ) : (
            <button type="button" className="mt-btn mt-btn--primary" onClick={handleNext}>
              {isEditing ? ui('backToSummary') : diet('next')}
            </button>
          )}
        </div>
      </>
    );
  }

  // Masked in Clarity recordings: the questionnaire and the plan carry health data (weight, height, age, sex),
  // which Clarity's terms forbid sending
  return (
    <div
      className={`mt-root mt-root--app mt-diet-page${isWizard ? ' mt-diet-page--wizard' : ''}`}
      data-clarity-mask="True"
    >
      <ModalTrialRestriction
        isOpen={isTrialRestrictionOpen}
        onOpenChange={onTrialRestrictionOpenChange}
        onClose={onTrialRestrictionClose}
        onSkipTrialSuccess={refetchMe}
        isCanceled={dataMe?.me?.cancelAtPeriodEnd}
      />
      <ModalUpgradePro
        isOpen={isUpgradeOpen}
        onOpenChange={onUpgradeOpenChange}
        onClose={onUpgradeClose}
        title={diet('proTitle')}
        description={diet('proDesc')}
        videoSrc={language === 'pt' ? dietVidBr : dietVidEn}
      />
      <Modal
        isOpen={blocker.state === 'blocked'}
        onOpenChange={(open) => !open && blocker.reset?.()}
        placement="center"
        hideCloseButton
        classNames={{ base: 'mt-root mt-dialog', backdrop: 'mt-dialog__backdrop' }}
      >
        <ModalContent>
          {() => (
            <div className="mt-dialog__body">
              <h2 className="mt-dialog__title">{isGenerating ? ui('leaveTitle') : t('pages.DietImport.dialog_leave_title')}</h2>
              <p className="mt-dialog__text">{isGenerating ? ui('leaveBody') : t('pages.DietImport.dialog_leave_text')}</p>
              <div className="mt-dialog__actions">
                <button type="button" className="mt-btn mt-btn--quiet" onClick={() => blocker.proceed?.()}>
                  {ui('leave')}
                </button>
                <button type="button" className="mt-btn mt-btn--primary" onClick={() => blocker.reset?.()}>
                  {ui('stay')}
                </button>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>
      <ConfirmDialog
        isOpen={isRemoveConfirmOpen}
        title={ui('removeConfirmTitle', { name: foodEdit?.item?.name })}
        text={ui('removeConfirmText')}
        confirmLabel={ui('editRemove')}
        cancelLabel={ui('removeCancel')}
        onCancel={() => setIsRemoveConfirmOpen(false)}
        onConfirm={() => {
          setIsRemoveConfirmOpen(false);
          removeFood();
        }}
      />
      {/* A meal keeps at least one food: the last one can be swapped, not removed */}
      <FoodEditSheet
        isOpen={isFoodEditOpen}
        onOpenChange={setIsFoodEditOpen}
        food={foodEdit?.item}
        mealName={foodEdit?.mealName}
        mealFoods={foodEdit?.mealFoods}
        canRemove={(foodEdit?.mealFoods?.length || 0) > 1}
        onSwap={swapFood}
        onAmount={changeFoodAmount}
        onRemove={() => setIsRemoveConfirmOpen(true)}
        substitutes={isOwnPlan(origin) ? substitutesFor(planSubstitutions, foodEdit?.item) : undefined}
        keepSource={isOwnPlan(origin)}
      />
      {isLocked ? (
        <div className="mt-dp-kept">
          <section className="mt-dp-kept__card" aria-labelledby="mt-diet-kept-title">
            <PreviewArt kind="rings" badge="lock" />
            <h2 id="mt-diet-kept-title" className="mt-dp-kept__title">
              {ui('keptTitle')}
            </h2>
            <p className="mt-dp-kept__text">{ui('keptBody')}</p>
            <button
              type="button"
              className="mt-btn mt-btn--primary"
              onClick={() => {
                trackEvent('diet_plan_kept_cta', 'Diet Plan - Kept Plan CTA', 'diet_plan', true);
                navigate('/plans');
              }}
            >
              {t('components.ModalUpgradePro.cta', { context: trialContext(dataMe?.me) })}
            </button>
          </section>
          {/* A plan's shape behind it, blurred and out of reach: bars, not the kept plan's meals */}
          <div className="mt-locked mt-dp-kept__under" aria-hidden="true" inert="">
            {renderKeptStandIn()}
          </div>
        </div>
      ) : (
        <div>{content}</div>
      )}
    </div>
  );
};
